import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useArmyStore } from '../store/armyStore';
import { calcArmourSave, calcCategoryPoints, calcEntryPoints, calcOptionsCost, entryArmourSave, entryHasMount, flattenEquipment, getEffectiveListCategory, getEntryRuleIds, isArmyOfInfamy, isBattleStandardBearer, isMountPointerOption, magicStandardCap, entryIsWizard as isWizardEntry, optionRequiresMount, optionRequiresStandardBearer, parseUnitSize, suggestedGeneralEntryId, unitUnlockMet, upgradeAllowanceOpts, upgradeCost, validateArmy } from '../utils/armyValidation';
import { getFaction } from '../data/factions/index';

import type { ErrataNote, Faction, Unit, WeaponProfile, Option, OptionChoice, SubOrder, Profile, KnightlyVirtue } from '../types/faction';
import type { ArmyEntry, ArmyList, RunicItemState } from '../types/army';
import specialRulesData from '../data/rules/special-rules.json';
import { getLore, unitLoreToId } from '../utils/magic';
import { isMagicItemEligible, itemsForFormat, itemsForOrigin, magicArmourAllowed } from '../utils/magicItemEligibility';
import { isOncePerArmy, maxPerModel, upgradeEligibility } from '../utils/upgradeEligibility';
import ValidationBars from '../components/ValidationBars';
import { generateArmyName } from '../data/armyNames';
import { generateArmyText, copyToClipboard, shareNative } from '../utils/dataTransfer';
import QRCodeModal from '../components/QRCodeModal';


type BrowserTab = 'characters' | 'core' | 'special' | 'rare' | 'mercenaries';

const SOURCE_ABBREV: Record<string, string> = {
  forces_of_fantasy: 'FoF',
  ravening_hordes: 'RH',
  arcane_journal: 'AJ',
  legends: 'Leg',
  renegades: 'Ren',
  battle_march: 'BM',
};

function SourceRef({ source, page }: { source: string; page?: number }) {
  if (!page) return null;
  return (
    <span style={{
      fontFamily: "'Cinzel', Georgia, serif",
      fontSize: '9px',
      letterSpacing: '0.06em',
      color: 'var(--f-text-4)',
      backgroundColor: 'var(--f-bg)',
      border: '1px solid var(--f-border)',
      borderRadius: '3px',
      padding: '1px 5px',
      whiteSpace: 'nowrap',
    }}>
      {SOURCE_ABBREV[source] ?? source} p.{page}
    </span>
  );
}

/** Errata badge - amber pill for stat/points changes, muted "FAQ" tag for rule changes */
function ErrataIndicator({ errata }: { errata?: ErrataNote[] }) {
  if (!errata || errata.length === 0) return null;

  const hasStatChange = errata.some((e) => e.type === 'stat' || e.type === 'points');
  const hasRuleChange = errata.some((e) => e.type === 'rule');
  const faqUrl = errata[0].faq_url;
  const tooltipLines = errata.map((e) => e.note).join('\n');

  return (
    <a
      href={faqUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={tooltipLines}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', textDecoration: 'none' }}
    >
      {hasStatChange && (
        <span style={{
          fontFamily: "'Cinzel', Georgia, serif",
          fontSize: '8px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#92400e',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '3px',
          padding: '1px 5px',
          whiteSpace: 'nowrap',
        }}>
          FAQ ↗
        </span>
      )}
      {hasRuleChange && !hasStatChange && (
        <span style={{
          fontFamily: "'Cinzel', Georgia, serif",
          fontSize: '8px',
          letterSpacing: '0.06em',
          color: 'var(--f-text-4)',
          border: '1px solid var(--f-border)',
          borderRadius: '3px',
          padding: '1px 4px',
          whiteSpace: 'nowrap',
        }}>
          FAQ ↗
        </span>
      )}
    </a>
  );
}

const TABS: { id: BrowserTab; label: string }[] = [
  { id: 'characters', label: 'Characters' },
  { id: 'core', label: 'Core' },
  { id: 'special', label: 'Special' },
  { id: 'rare', label: 'Rare' },
  { id: 'mercenaries', label: 'Mercenaries' },
];

/** Whether a unit is available in the given army composition */
function isUnitAvailable(unit: Unit, compositionId: string): boolean {
  if (unit.availability && unit.availability.length > 0) {
    return unit.availability.includes(compositionId);
  }
  return true;
}

/** Units available to browse for the given tab and composition (excludes mounts) */
function getUnitsForTab(
  faction: Faction, tab: BrowserTab, compositionId: string, subOrder?: SubOrder, army?: ArmyList,
): Unit[] {
  const isAoI = isArmyOfInfamy(faction.army_compositions.find((c) => c.id === compositionId));
  return faction.units.filter(
    (u) =>
      u.category !== 'mount' &&
      getEffectiveListCategory(u, compositionId, isAoI, subOrder) === tab &&
      isUnitAvailable(u, compositionId) &&
      // A knightly order's forbidden units (a White Wolf list may not take
      // priests of Sigmar) - the validator flags them, so the drawer must not
      // offer them
      !subOrder?.restrictions?.includes(u.id) &&
      // A promoted-slot row stays hidden until its condition is met
      (army === undefined || unitUnlockMet(u, army))
  );
}


export default function ArmyEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const armies = useArmyStore((s) => s.armies);
  const addEntry = useArmyStore((s) => s.addEntry);
  const removeEntry = useArmyStore((s) => s.removeEntry);
  const duplicateEntry = useArmyStore((s) => s.duplicateEntry);
  const updateEntry = useArmyStore((s) => s.updateEntry);
  const renameArmy = useArmyStore((s) => s.renameArmy);
  const setSubOrder = useArmyStore((s) => s.setSubOrder);
  const setGeneral = useArmyStore((s) => s.setGeneral);

  const army = armies.find((a) => a.id === id);
  const faction = army ? getFaction(army.factionId) : undefined;

  const [showQR, setShowQR] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<BrowserTab>('characters');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [expandedMagicCategories, setExpandedMagicCategories] = useState<Record<string, boolean>>({
    magic_weapon: false,
    magic_armour: false,
    talisman: false,
    enchanted_item: false,
    arcane_item: false,
    magic_standard: false,
    magic_items_section: false,
  });
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; key: number } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastKey = useRef(0);


  if (!army || !faction) {
    return (
      <div className="p-6">
        <p style={{ color: 'var(--f-text-3)' }}>{!army ? 'Army not found.' : 'Faction data not found.'}</p>
        <button onClick={() => navigate('/army-builder')} className="mt-3 text-sm underline" style={{ color: 'var(--f-primary)' }}>
          Back to My Armies
        </button>
      </div>
    );
  }

  const pts = calcCategoryPoints(army, faction);
  const issues = validateArmy(army, faction);
  const errors = issues.filter((i) => i.severity === 'error');
  const comp = faction.army_compositions.find((c) => c.id === army.compositionId);
  const subOrder = army.subOrderId
    ? comp?.sub_orders?.find((s) => s.id === army.subOrderId)
    : undefined;

  // army is guaranteed non-null past the early return above
  const armyId = army.id;

  function handleAddUnit(unit: Unit) {
    const { min } = parseUnitSize(unit.unit_size);
    addEntry(armyId, {
      unitId: unit.id,
      quantity: min,
      includeChampion: false,
      includeStandard: false,
      includeMusician: false,
      selectedOptions: [],
      selectedVirtueId: null,
      selectedMagicItemIds: [],
      selectedMountId: null,
    });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastKey.current += 1;
    setToast({ message: `+ ${unit.name} added`, key: toastKey.current });
    toastTimer.current = setTimeout(() => setToast(null), 2000);
  }

  function handleQtyChange(entry: ArmyEntry, unit: Unit, delta: number) {
    const { min, max } = parseUnitSize(unit.unit_size);
    const next = entry.quantity + delta;
    if (next < min || (max !== null && next > max)) return;
    updateEntry(armyId, entry.id, { quantity: next });
  }

  function openDrawer(tab: BrowserTab) {
    setDrawerTab(tab);
    setDrawerOpen(true);
  }

  // ---- sub-components ----

  const isOver = pts.total > army.pointsLimit;
  const remaining = army.pointsLimit - pts.total;

  const renderEditorHeader = () => (
    <div
      className="sticky top-0 z-30"
      style={{
        backgroundColor: 'var(--f-surface)',
        borderBottom: '1px solid var(--f-border)',
        transition: 'background 0.4s',
      }}
    >
      {/* Proclamation band */}
      <div style={{
        padding: '14px 24px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
        borderBottom: '1px solid var(--f-border)',
      }}>
        {/* Left: back + army name */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2px' }}>
            <button
              onClick={() => navigate('/army-builder')}
              style={{
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '9px',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--f-text-4)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
            >
              ← Armies
            </button>
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '8px',
              letterSpacing: '0.5em',
              textTransform: 'uppercase',
              color: 'var(--f-text-4)',
            }}>
              <span style={{ width: '20px', height: '1px', backgroundColor: 'var(--f-gold-rule)', display: 'inline-block' }} />
              Army Manifest
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {editingName ? (
              <input
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onBlur={() => {
                  if (nameInput.trim()) renameArmy(army.id, nameInput.trim());
                  setEditingName(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') { setEditingName(false); }
                }}
                style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '22px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  backgroundColor: 'var(--f-elevated)',
                  color: 'var(--f-text)',
                  border: '1px solid var(--f-gold)',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  flex: 1,
                  minWidth: 0,
                  outline: 'none',
                }}
              />
            ) : (
              <button
                onClick={() => { setNameInput(army.name); setEditingName(true); }}
                title="Click to rename"
                style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '22px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  color: 'var(--f-text)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  textAlign: 'left',
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {army.name}
              </button>
            )}
            <button
              onClick={() => {
                const name = generateArmyName(army.factionId);
                renameArmy(army.id, name);
                setNameInput(name);
              }}
              title="Generate a random name"
              style={{
                flexShrink: 0,
                width: '28px',
                height: '28px',
                border: '1px solid var(--f-border-mid)',
                borderRadius: '3px',
                backgroundColor: 'var(--f-elevated)',
                color: 'var(--f-gold)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--f-gold)';
                e.currentTarget.style.color = 'var(--f-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--f-border-mid)';
                e.currentTarget.style.color = 'var(--f-gold)';
              }}
            >
              ⚄
            </button>
            <button
              onClick={async () => {
                const text = generateArmyText(army, 'social');
                const shared = await shareNative(`${army.name} - Battle Standard`, text);
                if (!shared) await copyToClipboard(text);
              }}
              style={{
                flexShrink: 0,
                height: '28px',
                padding: '0 10px',
                border: '1px solid var(--f-border-mid)',
                borderRadius: '3px',
                backgroundColor: 'var(--f-elevated)',
                color: 'var(--f-gold)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '9.5px',
                letterSpacing: '0.08em',
                whiteSpace: 'nowrap',
                transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--f-gold)';
                e.currentTarget.style.color = 'var(--f-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--f-border-mid)';
                e.currentTarget.style.color = 'var(--f-gold)';
              }}
            >
              Share ↑
            </button>
            <button
              onClick={() => setShowQR(true)}
              style={{
                padding: '6px 12px', borderRadius: '4px', fontSize: '13px',
                fontFamily: "'Cinzel', serif", cursor: 'pointer',
                border: '1px solid var(--f-border-mid)', color: 'var(--f-gold)',
                background: 'transparent',
              }}
            >
              QR
            </button>
          </div>

          <p style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '12px',
            color: 'var(--f-text-3)',
            margin: 0,
          }}>
            {army.pointsLimit.toLocaleString()} point engagement · {comp?.name ?? 'Standard'}{subOrder ? ` · ${subOrder.name}` : ''}
          </p>
          {(() => {
            const hasChars = army.entries.some((e) => faction.units.find((u) => u.id === e.unitId)?.category === 'character');
            if (!hasChars) return null;
            const genEntry = army.generalEntryId ? army.entries.find((e) => e.id === army.generalEntryId) : undefined;
            const genUnit = genEntry ? faction.units.find((u) => u.id === genEntry.unitId) : undefined;
            const genName = genEntry ? (genEntry.customName || genUnit?.name || 'Unknown') : null;
            const suggestion = suggestedGeneralEntryId(army, faction);
            return (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '12px', fontStyle: 'italic', color: 'var(--f-text-3)' }}>
                  General: <span style={{ color: genName ? 'var(--f-gold)' : '#C0392B', fontWeight: 700 }}>{genName ?? 'none set'}</span>
                </span>
                {suggestion && army.generalEntryId !== suggestion && (
                  <button
                    onClick={() => setGeneral(armyId, suggestion)}
                    title="Designate the highest-Leadership character (excluding the Battle Standard Bearer) as General"
                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', color: 'var(--f-text)', background: 'var(--f-elevated)', border: '1px solid var(--f-border)', borderRadius: '3px', padding: '2px 8px', cursor: 'pointer' }}
                  >Auto (highest Leadership)</button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Right: points circle + unbound */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
          {/* Points circle */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              border: '2px solid var(--f-gold)',
              backgroundColor: 'var(--f-elevated)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              boxShadow: '0 0 0 4px var(--f-gold-dim), inset 0 1px 4px rgba(0,0,0,0.06)',
              transition: 'all 0.4s',
            }}>
              {/* Inner ring */}
              <div style={{
                position: 'absolute',
                inset: '4px',
                borderRadius: '50%',
                border: '1px solid var(--f-gold-rule)',
                pointerEvents: 'none',
              }} />
              <span style={{
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: 1,
                color: isOver ? '#C0392B' : 'var(--f-gold)',
                transition: 'color 0.4s',
              }}>{pts.total}</span>
              <span style={{
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '10px',
                color: 'var(--f-text-3)',
                lineHeight: 1,
              }}>/ {army.pointsLimit}</span>
            </div>
            <span style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '7px',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: 'var(--f-text-4)',
            }}>Points Bound</span>
          </div>

          {/* Remaining / over */}
          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '36px',
              fontWeight: 700,
              lineHeight: 1,
              color: isOver ? '#C0392B' : 'var(--f-primary)',
              margin: 0,
              transition: 'color 0.4s',
            }}>
              {isOver ? `-${Math.abs(remaining)}` : remaining}
            </p>
            <p style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '7px',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: isOver ? '#C0392B' : 'var(--f-text-4)',
              margin: 0,
            }}>
              {isOver ? 'Over Limit' : 'Unbound'}
            </p>
          </div>
        </div>
      </div>

      {/* Validation bars */}
      <ValidationBars pts={pts} army={army} faction={faction} />
    </div>
  );

  const renderValidationBanner = () => {
    if (errors.length === 0) return null;
    return (
      <div style={{
        padding: '8px 24px',
        borderBottom: '1px solid var(--f-border)',
        borderLeft: '4px solid #C0392B',
        backgroundColor: 'var(--f-elevated)',
      }}>
        {errors.map((e, i) => (
          <p key={i} style={{
            fontSize: '12px',
            color: '#C0392B',
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic',
            margin: '2px 0',
          }}>
            ⚠ {e.message}
          </p>
        ))}
      </div>
    );
  };

  const renderUnitBrowserDrawer = () => {
    const units = getUnitsForTab(faction, drawerTab, army.compositionId, subOrder, army);
    const tabColor = CAT_COLORS[drawerTab];
    return (
      /* Backdrop */
      <div
        onClick={() => setDrawerOpen(false)}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300, display: 'flex', justifyContent: 'flex-end' }}
      >
        {/* Drawer panel */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 'min(440px, 100vw)',
            backgroundColor: 'var(--f-surface)',
            borderLeft: '1px solid var(--f-border-mid)',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 301,
            animation: 'drawer-slide-in 0.25s ease',
            position: 'relative',
          }}
        >
          {/* Drawer header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 12px', borderBottom: '1px solid var(--f-border)', flexShrink: 0 }}>
            <p style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '10px', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--f-text-4)', margin: 0 }}>
              Enrol a Unit
            </p>
            <button
              onClick={() => setDrawerOpen(false)}
              style={{ background: 'none', border: '1px solid var(--f-border)', color: 'var(--f-text-3)', width: '28px', height: '28px', cursor: 'pointer', borderRadius: '3px', fontSize: '14px' }}
            >✕</button>
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--f-border)', flexShrink: 0 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setDrawerTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '10px 4px',
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: drawerTab === tab.id ? CAT_COLORS[tab.id] : 'var(--f-text-4)',
                  borderBottom: drawerTab === tab.id ? `2px solid ${CAT_COLORS[tab.id]}` : '2px solid transparent',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  border: 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Unit list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {units.map((unit) => {
              const { min } = parseUnitSize(unit.unit_size);
              const isFixed = unit.unit_size === '1';
              const minCost = isFixed ? unit.points : unit.points * min;
              const inListCount = army.entries.filter((e) => e.unitId === unit.id).length;
              return (
                <div key={unit.id} style={{ backgroundColor: 'var(--f-card)', border: '1px solid var(--f-border)', borderLeft: `3px solid ${tabColor}`, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                        <p style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '13px', fontWeight: 700, color: 'var(--f-text)', margin: 0, letterSpacing: '0.02em' }}>{unit.name}</p>
                        <SourceRef source={unit.source} page={unit.source_page} />
                        <ErrataIndicator errata={unit.errata} />
                      </div>
                      <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', fontStyle: 'italic', color: 'var(--f-text-3)', margin: '0 0 4px' }}>{unit.troop_type} · {unit.base_size}</p>
                      <p style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '11px', color: 'var(--f-gold)', margin: '0 0 4px' }}>
                        {isFixed ? `${unit.points} pts` : `${unit.points} pts/model · min ${min}${unit.unit_size !== `${min}+` && unit.unit_size !== `${min}` ? ` (${unit.unit_size})` : '+'}`}
                        {minCost !== unit.points && !isFixed ? ` · from ${minCost} pts` : ''}
                      </p>
                      <StatBar unit={unit} />
                      <SpecialRulesList ruleIds={unit.special_rules} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleAddUnit(unit)}
                        style={{ backgroundColor: 'var(--f-primary)', color: '#fff', border: 'none', padding: '6px 14px', fontFamily: "'Cinzel', Georgia, serif", fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                      >
                        + Enrol
                      </button>
                      {inListCount > 0 && (
                        <span style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '10px', color: 'var(--f-gold)', backgroundColor: 'var(--f-gold-dim)', padding: '2px 8px', border: '1px solid var(--f-gold-rule)' }}>
                          ×{inListCount} in list
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {units.length === 0 && (
              <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: 'italic', fontSize: '13px', textAlign: 'center', padding: '32px 0', color: 'var(--f-text-4)' }}>
                No units in this category
              </p>
            )}
          </div>

          {/* Toast */}
          {toast && (
            <div key={toast.key} style={{ position: 'absolute', bottom: '20px', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 10, padding: '0 16px' }}>
              <div style={{ backgroundColor: 'var(--f-primary)', color: '#fff', padding: '8px 20px', fontFamily: "'Cinzel', Georgia, serif", fontSize: '12px', letterSpacing: '0.05em', animation: 'toast-pop 2s ease forwards', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                {toast.message}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CAT_COLORS: Record<BrowserTab, string> = {
    characters:  'var(--f-cat-characters)',
    core:        'var(--f-cat-core)',
    special:     'var(--f-cat-special)',
    rare:        'var(--f-cat-rare)',
    mercenaries: 'var(--f-cat-mercenaries)',
  };

  const renderArmyList = () => {
    const hasMercsRule = comp?.rules.some((r) => r.category === 'mercenaries') ?? false;
    const categories: { id: BrowserTab; label: string }[] = [
      { id: 'characters', label: 'Characters' },
      { id: 'core', label: 'Core' },
      { id: 'special', label: 'Special' },
      { id: 'rare', label: 'Rare' },
      ...(hasMercsRule ? [{ id: 'mercenaries' as BrowserTab, label: 'Mercenaries' }] : []),
    ];

    return (
      <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px 10px', borderBottom: '1px solid var(--f-border)' }}>
          <p style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '10px',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--f-text-4)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: 0,
          }}>
            <span style={{ width: '20px', height: '1px', backgroundColor: 'var(--f-gold-rule)', display: 'inline-block' }} />
            Deployment Roster
          </p>
          <button
            onClick={() => openDrawer('characters')}
            style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '10px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--f-primary)',
              border: '1px solid var(--f-primary)',
              backgroundColor: 'transparent',
              padding: '5px 14px',
              cursor: 'pointer',
              borderRadius: '2px',
              transition: 'all 0.15s',
            }}
          >
            + Enrol Unit
          </button>
        </div>

        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Entries grouped by category */}
          {categories.map((cat) => {
            const entries = army.entries.filter((entry) => {
              const unit = faction.units.find((u) => u.id === entry.unitId);
              if (!unit) return false;
              return getEffectiveListCategory(unit, army.compositionId) === cat.id;
            });
            const catColor = CAT_COLORS[cat.id];

            const isCollapsed = !!collapsedSections[cat.id];
            return (
              <div key={cat.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {/* Heraldic category divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                  {/* Collapse toggle + label - clickable */}
                  <button
                    onClick={() => setCollapsedSections((prev) => ({ ...prev, [cat.id]: !prev[cat.id] }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0,
                    }}
                    aria-expanded={!isCollapsed}
                    aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${cat.label}`}
                  >
                    <span style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '9px', color: catColor, opacity: 0.7, userSelect: 'none', marginRight: '2px' }}>
                      {isCollapsed ? '▸' : '▾'}
                    </span>
                    <div style={{
                      fontFamily: "'Cinzel', Georgia, serif",
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      padding: '4px 14px',
                      border: `1px solid ${catColor}`,
                      color: catColor,
                      position: 'relative',
                      flexShrink: 0,
                    }}>
                      <span style={{ position: 'absolute', left: '-9px', top: '50%', transform: 'translateY(-50%)', fontSize: '7px' }}>◆</span>
                      {cat.label}
                      <span style={{ position: 'absolute', right: '-9px', top: '50%', transform: 'translateY(-50%)', fontSize: '7px' }}>◆</span>
                    </div>
                  </button>
                  <div style={{ flex: 1, height: '1px', opacity: 0.2, background: `linear-gradient(90deg, ${catColor}, transparent)` }} />
                  {/* Per-section enrol button */}
                  <button
                    onClick={() => openDrawer(cat.id)}
                    style={{
                      fontFamily: "'Cinzel', Georgia, serif",
                      fontSize: '9px',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: catColor,
                      border: `1px solid ${catColor}`,
                      backgroundColor: 'transparent',
                      padding: '3px 10px',
                      cursor: 'pointer',
                      borderRadius: '2px',
                      opacity: 0.75,
                      transition: 'opacity 0.15s',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.75')}
                  >
                    + Enrol
                  </button>
                  <span style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '11px', color: 'var(--f-text-3)', flexShrink: 0 }}>
                    {pts[cat.id]} pts
                  </span>
                </div>

                {!isCollapsed && entries.length === 0 && (
                  <button
                    onClick={() => openDrawer(cat.id)}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: `1px dashed ${catColor}`,
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontFamily: "'Cinzel', Georgia, serif",
                      fontSize: '10px',
                      letterSpacing: '0.3em',
                      textTransform: 'uppercase',
                      color: catColor,
                      opacity: 0.6,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                  >
                    ◆ Enrol a {cat.label} ◆
                  </button>
                )}
                {!isCollapsed && entries.map((entry) => {
                  const unit = faction.units.find((u) => u.id === entry.unitId);
                  if (!unit) return null;
                  const entryPts = calcEntryPoints(unit, entry, faction);
                  const optsCost = calcOptionsCost(unit, entry, faction);
                  const isFixed = unit.unit_size === '1';
                  const showOptions = !!(unit.options && unit.options.length > 0) || !!(unit.command && unit.command.length > 0);
                  const { min, max } = parseUnitSize(unit.unit_size);

                  const mountUnit = entry.selectedMountId
                    ? faction.units.find((u) => u.id === entry.selectedMountId)
                    // Fallback: some characters have mount as a plain checkbox/choice option
                    // Match either exact name ("Warhorse") or "Mount on X" / "Mount on a X" style
                    : faction.units.find((u) =>
                        u.category === 'mount' &&
                        entry.selectedOptions.some((o) => {
                          const ol = o.toLowerCase();
                          const nl = u.name.toLowerCase();
                          return ol === nl || ol.endsWith(nl);
                        })
                      ) ?? null;
                  const entrySave = entryArmourSave(unit, entry, faction);
                  const entryHonour = getSelectedHonour(entry, faction);

                  return (
                    <div
                      key={entry.id}
                      style={{
                        backgroundColor: 'var(--f-card)',
                        border: '1px solid var(--f-border)',
                        borderLeft: `4px solid ${catColor}`,
                        padding: '12px 16px',
                      }}
                    >
                      {/* Unit header row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'start', marginBottom: '6px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                            <EntryName
                              displayName={entry.customName || unit.name}
                              baseName={unit.name}
                              isCustom={Boolean(entry.customName)}
                              onRename={(name) => updateEntry(armyId, entry.id, { customName: name })}
                            />
                            <SourceRef source={unit.source} page={unit.source_page} />
                            <ErrataIndicator errata={unit.errata} />
                          </div>
                          <p style={{
                            fontFamily: "'Source Serif 4', Georgia, serif",
                            fontSize: '11px',
                            fontStyle: 'italic',
                            color: 'var(--f-text-3)',
                            margin: 0,
                          }}>
                            {entry.customName ? `${unit.name} · ` : ''}{unit.troop_type} · {unit.base_size}
                            {!isFixed ? ` · ${entry.quantity} models` : ''}
                            {entrySave !== '-' ? ` · Sv ${entrySave}` : ''}
                          </p>
                          {unit.category === 'character' && (() => {
                            const isGeneral = army.generalEntryId === entry.id;
                            const isBsb = isBattleStandardBearer(entry);
                            return (
                              <div style={{ marginTop: '6px' }}>
                                {isGeneral ? (
                                  <button
                                    onClick={() => setGeneral(armyId, null)}
                                    title="This character is your General. Click to remove."
                                    style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: '#1b1b1b', background: 'var(--f-gold)', border: '1px solid var(--f-gold)', borderRadius: '3px', padding: '2px 8px', cursor: 'pointer' }}
                                  >★ General</button>
                                ) : (
                                  <button
                                    onClick={() => { if (!isBsb) setGeneral(armyId, entry.id); }}
                                    disabled={isBsb}
                                    title={isBsb ? 'The Battle Standard Bearer cannot be the General' : 'Designate this character as your General'}
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '10px', fontStyle: 'italic', color: isBsb ? 'var(--f-text-4)' : 'var(--f-text-3)', background: 'none', border: '1px dashed var(--f-border)', borderRadius: '3px', padding: '2px 8px', cursor: isBsb ? 'not-allowed' : 'pointer', opacity: isBsb ? 0.5 : 1 }}
                                  >{isBsb ? 'BSB - cannot be General' : 'Set as General'}</button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          <span style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '22px', fontWeight: 700, lineHeight: 1, color: 'var(--f-gold)' }}>
                            {entryPts}
                          </span>
                          <span style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '8px', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--f-text-4)' }}>
                            pts
                          </span>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                            <button onClick={() => duplicateEntry(armyId, entry.id)} title="Duplicate"
                              style={{ width: '26px', height: '26px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: 'var(--f-text-3)', fontSize: '13px', cursor: 'pointer', borderRadius: '3px' }}>⧉</button>
                            <button onClick={() => removeEntry(army.id, entry.id)} aria-label="Remove"
                              style={{ width: '26px', height: '26px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: '#C0392B', fontSize: '13px', cursor: 'pointer', borderRadius: '3px' }}>✕</button>
                          </div>
                        </div>
                      </div>

                      {!isFixed && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', fontStyle: 'italic', color: 'var(--f-text-3)' }}>Models:</span>
                          <button onClick={() => handleQtyChange(entry, unit, -1)} disabled={entry.quantity <= min}
                            style={{ width: '24px', height: '24px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: 'var(--f-text)', fontWeight: 700, cursor: 'pointer', borderRadius: '3px', opacity: entry.quantity <= min ? 0.3 : 1 }}>−</button>
                          <input
                            type="number"
                            min={min}
                            max={max ?? undefined}
                            value={entry.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val)) {
                                const clamped = Math.max(min, max !== null ? Math.min(max, val) : val);
                                updateEntry(armyId, entry.id, { quantity: clamped });
                              }
                            }}
                            style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: '13px', fontWeight: 700, color: 'var(--f-text)', width: '48px', textAlign: 'center', background: 'var(--f-elevated)', border: '1px solid var(--f-border)', borderRadius: '3px', padding: '2px 4px' }}
                          />
                          <button onClick={() => handleQtyChange(entry, unit, -5)} disabled={entry.quantity <= min}
                            style={{ height: '24px', padding: '0 6px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: 'var(--f-text)', fontWeight: 700, fontSize: '11px', cursor: 'pointer', borderRadius: '3px', opacity: entry.quantity <= min ? 0.3 : 1 }}>-5</button>
                          <button onClick={() => handleQtyChange(entry, unit, 1)} disabled={max !== null && entry.quantity >= max}
                            style={{ width: '24px', height: '24px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: 'var(--f-text)', fontWeight: 700, cursor: 'pointer', borderRadius: '3px', opacity: (max !== null && entry.quantity >= max) ? 0.3 : 1 }}>+</button>
                          <button onClick={() => handleQtyChange(entry, unit, 5)} disabled={max !== null && entry.quantity >= max}
                            style={{ height: '24px', padding: '0 6px', border: '1px solid var(--f-border)', backgroundColor: 'var(--f-elevated)', color: 'var(--f-text)', fontWeight: 700, fontSize: '11px', cursor: 'pointer', borderRadius: '3px', opacity: (max !== null && entry.quantity >= max) ? 0.3 : 1 }}>+5</button>
                          <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', color: 'var(--f-text-3)' }}>× {unit.points} pts</span>
                        </div>
                      )}

                      {(() => {
                        const champCmd = unit.command?.find((c) => c.role === 'champion');
                        // Champion stats come as overrides on the command entry or as a
                        // full is_champion profile row (Boss on O&G cavalry mobs).
                        const champRow = unit.profiles.find((p) => p.is_champion);
                        const champProfile = !entry.includeChampion
                          ? null
                          : champCmd?.champion_stats && unit.profiles[0]
                            ? { ...unit.profiles[0].profile, ...champCmd.champion_stats }
                            : champRow?.profile ?? null;
                        // Profile modifiers stack: the selected honour's plus any
                        // carried magic items' (Mantle of Drifting Shadow +1 M).
                        const mods: Record<string, number> = { ...(entryHonour?.stat_modifiers ?? {}) };
                        for (const itemId of entry.selectedMagicItemIds ?? []) {
                          const it = faction.magic_items.find((i) => i.id === itemId);
                          for (const [k, v] of Object.entries(it?.stat_modifiers ?? {})) {
                            mods[k] = (mods[k] ?? 0) + v;
                          }
                        }
                        return <StatBar unit={unit} save={entrySave} mount={mountUnit} championProfile={champProfile} championName={champCmd?.name ?? 'Champion'} statModifiers={Object.keys(mods).length ? mods : undefined} />;
                      })()}

                      {/* Lore selector - shown for wizards with more than one lore option */}
                      {unit.magic && unit.magic.lores.length > 1 && (() => {
                        const activeLoreKey = entry.selectedLoreKey ?? unit.magic.lores[0];
                        const activeLoreId = unitLoreToId(activeLoreKey);
                        const loreUrl = `https://tow.whfb.app/the-lores-of-magic/${activeLoreId}`;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                            <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', fontStyle: 'italic', color: 'var(--f-text-3)', flexShrink: 0 }}>Lore:</span>
                            <select
                              value={activeLoreKey}
                              onChange={(e) => updateEntry(armyId, entry.id, { selectedLoreKey: e.target.value })}
                              style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '12px', color: 'var(--f-text)', background: 'var(--f-elevated)', border: '1px solid var(--f-border)', borderRadius: '3px', padding: '2px 6px', cursor: 'pointer' }}
                            >
                              {unit.magic.lores.map((loreKey) => (
                                <option key={loreKey} value={loreKey}>
                                  {getLore(loreKey)?.name ?? loreKey}
                                </option>
                              ))}
                            </select>
                            <a
                              href={loreUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View lore on tow.whfb.app"
                              style={{ color: 'var(--f-blue)', fontSize: '13px', lineHeight: 1, textDecoration: 'none', flexShrink: 0 }}
                            >↗</a>
                          </div>
                        );
                      })()}

                      {unit.weapon_profiles && unit.weapon_profiles.length > 0 && (() => {
                        // Show profiles matching base equipment OR currently selected weapon options
                        const equipped = [
                          ...flattenEquipment(unit.equipment),
                          ...entry.selectedOptions,
                        ].map(e => e.toLowerCase());
                        const relevantProfiles = unit.weapon_profiles.filter(wp => {
                          // Strip parenthetical qualifiers before matching so that e.g.
                          // "Polearm (single-handed)" matches equipment "Polearms (see weapon profiles)"
                          const wpRoot = wp.name.toLowerCase().replace(/\s*\(.*?\)/g, '').trim();
                          return equipped.some(eq => eq.includes(wpRoot));
                        });
                        return relevantProfiles.length > 0 ? <WeaponProfileTable profiles={relevantProfiles} /> : null;
                      })()}

                      <SpecialRulesList ruleIds={getEntryRuleIds(unit, entry, faction)} />

                      {/* Separate models this unit has bought - Weapon Teams, Fanatics.
                          They fight as their own model but deploy with this unit. */}
                      {getAttachedUnits(unit, entry, faction).map(({ attached, count }) => (
                        <div
                          key={attached.id}
                          className="mt-2 pt-2 pl-2 border-t border-l"
                          style={{ borderColor: 'var(--f-border)' }}
                        >
                          <p
                            className="text-xs font-semibold"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--f-text-2)' }}
                          >
                            {count > 1 ? `${count}x ` : ''}{attached.name}
                            <span className="font-normal italic ml-1" style={{ color: 'var(--f-text-3)' }}>
                              - attached model, {attached.troop_type}
                            </span>
                          </p>
                          <StatBar unit={attached} />
                          {attached.weapon_profiles && attached.weapon_profiles.length > 0 && (
                            <WeaponProfileTable profiles={attached.weapon_profiles} />
                          )}
                          <SpecialRulesList ruleIds={attached.special_rules ?? []} />
                        </div>
                      ))}

                      {showOptions && (
                        <>
                          <button
                            onClick={() => setExpandedEntryId(expandedEntryId === entry.id ? null : entry.id)}
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '11px', fontStyle: 'italic', color: 'var(--f-primary)', marginTop: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'left' }}
                          >
                            {expandedEntryId === entry.id ? '▲ Options' : '▼ Options'}
                            {optsCost > 0 && ` (+${optsCost} pts)`}
                          </button>
                          {expandedEntryId === entry.id && (
                            <EntryOptionsPanel
                              unit={unit}
                              entry={entry}
                              faction={faction}
                              armyId={armyId}
                              allEntries={army.entries}
                              isBattleMarch={army.matchedPlayFormats.includes('battle_march')}
                              updateEntry={updateEntry}
                              expandedMagicCategories={expandedMagicCategories}
                              setExpandedMagicCategories={setExpandedMagicCategories}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

        </div>
      </div>
    );
  };

  // Sub-order selector - mandatory before building when composition has sub_orders
  if (comp?.sub_orders && !army.subOrderId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {renderEditorHeader()}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <h2 style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '18px',
            color: 'var(--f-accent)',
            marginBottom: '4px',
          }}>
            Choose Your Knightly Order
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--f-text-3)', marginBottom: '16px' }}>
            Select the order your army belongs to. This cannot be changed once units are added.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {comp.sub_orders.map((order) => (
              <button
                key={order.id}
                onClick={() => setSubOrder(armyId, order.id)}
                style={{
                  background: 'var(--f-surface)',
                  border: '1px solid var(--f-border)',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'var(--f-text)',
                }}
              >
                <div style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--f-accent)',
                  marginBottom: '6px',
                }}>
                  {order.name}
                  <span style={{ fontFamily: 'inherit', fontWeight: 400, fontSize: '12px', color: 'var(--f-text-3)', marginLeft: '8px' }}>
                    +{order.character_upgrade_pts}pts character · +{order.unit_upgrade_pts_per_model}pts/model knights
                  </span>
                </div>
                {order.all_units_rules.length > 0 && (
                  <ul style={{ margin: '0 0 4px 0', paddingLeft: '16px', fontSize: '12px', color: 'var(--f-text-2)' }}>
                    {order.all_units_rules.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                )}
                {order.elite_rules.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--f-text-3)', fontStyle: 'italic' }}>
                    Grand Master / Chapter Master / Inner Circle Knights: {order.elite_rules.join('; ')}
                  </div>
                )}
                {order.restrictions.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                    Excludes: {order.restrictions.join(', ')}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {renderEditorHeader()}
      {renderValidationBanner()}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {renderArmyList()}
      </div>
      {drawerOpen && renderUnitBrowserDrawer()}
      {showQR && <QRCodeModal army={army} onClose={() => setShowQR(false)} />}
    </div>
  );
}

function StatBar({ unit, save, mount, championProfile, championName, statModifiers }: { unit: Unit; save?: string; mount?: Unit | null; championProfile?: Profile | null; championName?: string; statModifiers?: Record<string, number> }) {
  const main = unit.profiles[0];
  if (!main) return null;

  // Find mount profile (cavalry) or crew profile (war machine)
  const mountEntry = unit.profiles.find((pe) => pe.is_mount) ?? null;
  const crewEntry = unit.category === 'war_machine'
    ? (unit.profiles.slice(1).find((pe) => !pe.is_mount && !pe.is_champion) ?? null)
    : null;

  // For characters with a selected mount unit, synthesise a mount profile entry
  const charMountEntry =
    !mountEntry && mount?.profiles[0]
      ? { name: mount.name, profile: mount.profiles[0].profile, is_mount: true as const }
      : null;

  const extraEntry = mountEntry ?? crewEntry ?? charMountEntry;
  const multiRow = extraEntry !== null || !!championProfile;

  const cols = ['M', 'WS', 'BS', 'S', 'T', 'W', 'I', 'A', 'Ld', 'Sv'] as const;
  const displaySave = save ?? calcArmourSave(unit.equipment, unit.special_rules ?? [], unit.troop_type);

  // War machines move at crew speed - show crew M in the machine row
  const mainStats = { ...main.profile } as Record<string, string | number>;
  if (crewEntry && String(mainStats.M) === '-') {
    mainStats.M = crewEntry.profile.M;
  }
  // Honour-family characteristic modifiers (e.g. Blood of Caledor: +1 WS)
  for (const [k, delta] of Object.entries(statModifiers ?? {})) {
    const cur = parseInt(String(mainStats[k]), 10);
    if (!Number.isNaN(cur)) mainStats[k] = cur + delta;
  }

  // Label for the extra row
  const extraLabel = mountEntry
    ? mountEntry.name
    : charMountEntry
      ? charMountEntry.name
      : crewEntry
        ? `Crew ×${crewEntry.profile.W}`
        : '';

  const cellStyle: React.CSSProperties = {
    border: '1px solid var(--f-border)',
    textAlign: 'center',
    padding: '3px 4px',
  };
  const nameCellStyle: React.CSSProperties = {
    ...cellStyle,
    fontFamily: "'Cinzel', Georgia, serif",
    fontSize: '8px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--f-text-3)',
    textAlign: 'left',
    paddingLeft: '5px',
    width: '62px',
    maxWidth: '62px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const renderRow = (stats: typeof main.profile | Record<string, string | number>, rowSave: string, label: string) => (
    <tr>
      {multiRow && <td style={nameCellStyle} title={label}>{label}</td>}
      {cols.map((s) => {
        const val = s === 'Sv' ? rowSave : String(stats[s as keyof typeof stats] ?? '-');
        return (
          <td key={s} style={{
            ...cellStyle,
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '13px',
            fontWeight: 700,
            color: s === 'Sv' && val === '-' ? 'var(--f-text-4)' : 'var(--f-text)',
          }}>
            {val}
          </td>
        );
      })}
    </tr>
  );

  return (
    <table style={{ borderCollapse: 'collapse', marginTop: '8px', width: '100%', tableLayout: 'fixed' }}>
      <thead>
        <tr>
          {multiRow && <th style={{ ...cellStyle, width: '62px', backgroundColor: 'var(--f-bg)' }} />}
          {cols.map((s) => (
            <th key={s} style={{
              ...cellStyle,
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '9px',
              letterSpacing: '0.05em',
              color: 'var(--f-text-4)',
              fontWeight: 600,
              backgroundColor: 'var(--f-bg)',
            }}>
              {s}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {renderRow(mainStats, displaySave, main.name)}
        {championProfile && renderRow(championProfile, displaySave, championName ?? 'Champion')}
        {extraEntry && renderRow(extraEntry.profile, '-', extraLabel)}
      </tbody>
    </table>
  );
}

function formatRuleName(rule: string): string {
  return rule.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function WeaponProfileTable({ profiles }: { profiles: WeaponProfile[] }) {
  if (!profiles || profiles.length === 0) return null;
  return (
    <div className="mt-1.5 overflow-x-auto">
      <table className="text-xs w-full" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
            <th className="text-left pr-2 pb-0.5 font-medium whitespace-nowrap">Weapon</th>
            <th className="text-center pr-2 pb-0.5 font-medium whitespace-nowrap">Rng</th>
            <th className="text-center pr-2 pb-0.5 font-medium">S</th>
            <th className="text-center pr-2 pb-0.5 font-medium">AP</th>
            <th className="text-left pb-0.5 font-medium">Special</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((wp, i) => (
            <tr key={i}>
              <td className="pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)' }}>{wp.name}</td>
              <td className="text-center pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)' }}>{wp.range}</td>
              <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{wp.S}</td>
              <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{wp.AP}</td>
              <td className="py-0.5" style={{ color: 'var(--f-text-3)' }}>
                {wp.special_rules.map(formatRuleName).join(', ')}
                {wp.notes && <span className="italic opacity-70"> - {wp.notes}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function SpecialRulesList({ ruleIds }: { ruleIds: string[] }) {
  const [activeRule, setActiveRule] = useState<{ name: string; url: string; faqUrl?: string } | null>(null);
  if (!ruleIds || ruleIds.length === 0) return null;
  const rulesData = (specialRulesData as { rules: { id: string; name: string; url?: string; faq_url?: string }[] }).rules;
  return (
    <>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {ruleIds.map((id) => {
          const rule = rulesData.find((r) => r.id === id);
          const name = rule?.name ?? formatRuleName(id);
          const faqUrl = rule?.faq_url;
          // Use explicit url override if present, otherwise derive from name
          const url = rule?.url ?? (() => {
            // Strip parenthetical qualifier (e.g. "Hatred (Dwarfs)" → "hatred") so variants
            // resolve to the single base rule page on tow.whfb.app
            const baseName = name.replace(/\s*\(.*\)\s*$/, '').trim();
            const slug = baseName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            return `https://tow.whfb.app/special-rules/${slug}`;
          })();
          return (
            <button
              key={id}
              onClick={() => setActiveRule({ name, url, faqUrl })}
              className="text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1"
              style={{
                backgroundColor: 'var(--f-bg)',
                color: 'var(--f-primary)',
                border: '1px solid var(--f-border)',
                cursor: 'pointer',
              }}
            >
              {name}
              {faqUrl && (
                <span
                  className="text-xs font-bold px-0.5 rounded"
                  style={{ backgroundColor: '#92400e', color: '#fef3c7', fontSize: '0.6rem', lineHeight: '1rem' }}
                  title="Rule text updated in FAQ"
                >
                  FAQ
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeRule && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setActiveRule(null)}
        >
          <div
            className="flex flex-col rounded-lg overflow-hidden w-full max-w-2xl shadow-2xl"
            style={{ height: '80vh', backgroundColor: 'var(--f-elevated)', border: '1px solid var(--f-border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-4 py-2 shrink-0 border-b"
              style={{ borderColor: 'var(--f-border)' }}
            >
              <span className="text-sm font-semibold" style={{ color: 'var(--f-text)' }}>
                {activeRule.name}
              </span>
              <div className="flex items-center gap-3">
                {activeRule.faqUrl && (
                  <a
                    href={activeRule.faqUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: '#92400e', color: '#fef3c7' }}
                    title="This rule was updated in the Jan 2026 FAQ"
                  >
                    FAQ updated ↗
                  </a>
                )}
                <a
                  href={activeRule.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs"
                  style={{ color: 'var(--f-text-3)' }}
                >
                  Open in browser ↗
                </a>
                <button
                  onClick={() => setActiveRule(null)}
                  className="text-sm font-bold px-2 py-0.5 rounded"
                  style={{ color: 'var(--f-text)', backgroundColor: 'var(--f-bg)' }}
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              src={activeRule.url}
              className="flex-1 w-full border-0"
              title={activeRule.name}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** Honour-family lists: an option whose description contains `match` opens a picker over faction[key] */
const HONOUR_FAMILIES: {
  match: string;
  label: string;
  key: 'knightly_virtues' | 'elven_honours' | 'infamous_origins' | 'disciplines_of_the_old_ones'
    | 'big_names';
}[] = [
  { match: 'Knightly Virtue', label: 'Knightly Virtue', key: 'knightly_virtues' },
  { match: 'Elven Honour', label: 'Elven Honour', key: 'elven_honours' },
  { match: 'Infamous Origin', label: 'Infamous Origin', key: 'infamous_origins' },
  { match: 'Discipline of the Old Ones', label: 'Discipline of the Old Ones', key: 'disciplines_of_the_old_ones' },
  { match: 'Big Name', label: 'Big Name', key: 'big_names' },
];

/** Budgeted upgrade families — pick as many as the model's allowance affords.
 *  Unlike honours these are multi-select, so they live on selectedUpgradeIds.
 *  `match` is tested against the allowance option's description, which names the
 *  family ("Purchase Incantation Scrolls", "A Nymph may take Forest Spites…"). */
const UPGRADE_FAMILIES: {
  match: RegExp;
  label: string;
  key: 'gifts_of_chaos' | 'chaos_mutations' | 'forest_sprites' | 'vampiric_powers'
    | 'chaotic_traits' | 'daemonic_gifts' | 'incantation_scrolls' | 'disciplines_of_the_old_ones';
}[] = [
  { match: /gifts of chaos/i, label: 'Gifts of Chaos', key: 'gifts_of_chaos' },
  { match: /chaos mutations/i, label: 'Chaos Mutations', key: 'chaos_mutations' },
  // The books call these Forest Spites; the data key is forest_sprites.
  { match: /forest spites/i, label: 'Forest Spites', key: 'forest_sprites' },
  { match: /vampiric powers/i, label: 'Vampiric Powers', key: 'vampiric_powers' },
  { match: /chaotic traits/i, label: 'Chaotic Traits', key: 'chaotic_traits' },
  { match: /daemonic gifts/i, label: 'Daemonic Gifts', key: 'daemonic_gifts' },
  { match: /incantation scrolls/i, label: 'Incantation Scrolls', key: 'incantation_scrolls' },
  // Base Lizardmen lists sell a single Discipline (honour picker above); the
  // Renegades draft sells "0-3 Disciplines... up to 100 points" - a budget.
  { match: /disciplines? of the old ones/i, label: 'Disciplines of the Old Ones', key: 'disciplines_of_the_old_ones' },
];

/** Unit name with inline rename (customisation) - click ✎ to edit, Enter/blur to save, Esc to cancel.
 *  Clearing the field reverts to the unit's real name. */
function EntryName({ displayName, baseName, isCustom, onRename }: {
  displayName: string;
  baseName: string;
  isCustom: boolean;
  onRename: (name: string | undefined) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(displayName);

  const nameStyle: React.CSSProperties = {
    fontFamily: "'Cinzel', Georgia, serif",
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--f-text)',
    margin: 0,
    letterSpacing: '0.02em',
  };

  function commit() {
    const trimmed = draft.trim();
    onRename(trimmed && trimmed !== baseName ? trimmed : undefined);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        maxLength={60}
        aria-label={`Rename ${baseName}`}
        style={{
          ...nameStyle,
          backgroundColor: 'var(--f-elevated)',
          border: '1px solid var(--f-gold)',
          borderRadius: '3px',
          padding: '1px 6px',
          minWidth: '160px',
        }}
      />
    );
  }

  return (
    <>
      <p style={nameStyle}>{displayName}</p>
      <button
        onClick={() => { setDraft(isCustom ? displayName : ''); setEditing(true); }}
        title={isCustom ? `Rename (unit: ${baseName})` : 'Rename unit'}
        aria-label={`Rename ${baseName}`}
        style={{
          border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px',
          color: 'var(--f-text-4)', fontSize: '11px', lineHeight: 1,
        }}
      >✎</button>
    </>
  );
}

/** The honour-family upgrade selected on an entry, looked up across all of the faction's lists */
function getSelectedHonour(entry: ArmyEntry, faction: Faction): KnightlyVirtue | undefined {
  if (!entry.selectedVirtueId) return undefined;
  return HONOUR_FAMILIES.flatMap((f) => faction[f.key] ?? []).find((v) => v.id === entry.selectedVirtueId);
}

/** Canonical selectedOptions string for an honour's free equipment */
function honourEquipDesc(name: string, honour: KnightlyVirtue): string {
  return `${name} (${honour.name})`;
}

/** Every free-equipment desc any of the faction's honours could have written into selectedOptions */
function allHonourEquipDescs(faction: Faction): Set<string> {
  const out = new Set<string>();
  for (const h of HONOUR_FAMILIES.flatMap((f) => faction[f.key] ?? [])) {
    for (const fe of h.free_equipment ?? []) out.add(honourEquipDesc(fe.name, h));
  }
  return out;
}

/** Returns mount units available to the given character based on mount notes */
/**
 * Separate models this entry has bought through its options - a Skaven Weapon
 * Team, a Night Goblin Fanatic. They are their own model with their own profile
 * and rules, so the parent unit's card shows them rather than folding their
 * rules into the unit's own.
 */
function getAttachedUnits(unit: Unit, entry: ArmyEntry, faction: Faction): { attached: Unit; count: number }[] {
  const out: { attached: Unit; count: number }[] = [];
  const add = (id: string | undefined, count: number) => {
    if (!id || count <= 0) return;
    const attached = faction.units.find((u) => u.id === id);
    if (attached) out.push({ attached, count });
  };
  for (const o of unit.options ?? []) {
    if (o.per_n_models !== undefined) {
      add(o.attaches_unit_id, entry.optionQuantities?.[o.description] ?? 0);
    } else if (entry.selectedOptions.includes(o.description)) {
      add(o.attaches_unit_id, 1);
    }
    for (const c of o.choices ?? []) {
      if (entry.selectedOptions.includes(c.description)) add(c.attaches_unit_id, 1);
    }
  }
  return out;
}

function getAvailableMounts(unit: Unit, faction: Faction): Unit[] {
  const wanted = singularise(unit.name);
  return faction.units.filter((u) => {
    if (u.category !== 'mount') return false;
    const notes = u.notes ?? [];
    if (notes.length === 0) return true;
    const avail = notes.find((n) => n.toLowerCase().includes('available to'));
    if (!avail) return false;
    return avail
      .toLowerCase()
      .split('available to')[1]
      .replace(/\([^)]*\)/g, '')
      .replace(/ and /g, ',')
      .split(',')
      .map((s) => singularise(s.replace(/\bonly\b/g, '').replace(/[:.]/g, '')))
      .includes(wanted);
  });
}

/** Lowercase, trim and singularise a name for exact mount-eligibility comparison.
    Exact comparison (not substring) so that e.g. "Mage" does not match "Archmage". */
function singularise(name: string): string {
  const t = name.toLowerCase().trim();
  return t.endsWith('s') ? t.slice(0, -1) : t;
}

const MAGIC_ITEM_CATEGORY_LABELS: Record<string, string> = {
  magic_weapon: 'Magic Weapons',
  magic_armour: 'Magic Armour',
  talisman: 'Talismans',
  enchanted_item: 'Enchanted Items',
  arcane_item: 'Arcane Items',
  magic_standard: 'Magic Standards',
};

function EntryOptionsPanel({
  unit,
  entry,
  faction,
  armyId,
  allEntries,
  isBattleMarch,
  updateEntry,
  expandedMagicCategories,
  setExpandedMagicCategories,
}: {
  unit: Unit;
  entry: ArmyEntry;
  faction: Faction;
  armyId: string;
  allEntries: import('../types/army').ArmyEntry[];
  isBattleMarch: boolean;
  updateEntry: (armyId: string, entryId: string, patch: Partial<Omit<ArmyEntry, 'id'>>) => void;
  expandedMagicCategories: Record<string, boolean>;
  setExpandedMagicCategories: (categories: Record<string, boolean>) => void;
}) {
  const options = unit.options ?? [];
  // Battle March items are only legal in a Battle March muster list.
  // Battle March items are only legal in a Battle March muster list, and in a
  // two-list Army of Infamy a model may only buy from its own parent list.
  // Where a unit's origin is fixed by the book it came from (Hordes of Chaos) use
  // that; where it is a player choice (Renegade Crowns Infamous Origins) use the
  // faction the selected origin unlocks.
  const availableMagicItems = itemsForOrigin(
    itemsForFormat(faction.magic_items, isBattleMarch),
    (unit as { origin?: string }).origin,
    getSelectedHonour(entry, faction)?.unlocks_origin,
  );

  // A magic-items allowance option always names "magic item(s)". Budgeted upgrade
  // families (Forest Spites, Gifts of Chaos, Chaos Mutations, Vampiric Powers) and
  // Dwarf runes also carry max_points, so match on the phrase rather than on any
  // capped option - otherwise a Branchwraith's Spites budget or a Dwarf's rune
  // budget is misread as a magic-items allowance.
  const magicAllowanceOpt = options.find(
    (o) => o.max_points !== undefined && o.description.toLowerCase().includes('magic item')
  );
  const honourFamily = HONOUR_FAMILIES.find((f) => options.some((o) => o.description.includes(f.match)));
  const honourList = honourFamily ? (faction[honourFamily.key] ?? []) : [];
  const selectedHonour = honourList.find((v) => v.id === entry.selectedVirtueId);

  // Budgeted upgrade families (Gifts of Chaos, Vampiric Powers, Incantation
  // Scrolls, Chaotic Traits, …). A unit may hold several allowances - a Chaos
  // Lord has both a Gifts points budget and a Traits count - so the picker
  // renders one section per allowance, each tracking only its own family.
  const upgradeAllowances = upgradeAllowanceOpts(unit);
  const selectedUpgradeIds = entry.selectedUpgradeIds ?? [];
  const upgradeSections = upgradeAllowances.flatMap((allowance) => {
    const family = UPGRADE_FAMILIES.find((f) => f.match.test(allowance.description));
    const list = family ? (faction[family.key] ?? []) : [];
    if (!family || list.length === 0) return [];
    const spent = selectedUpgradeIds.reduce((sum, id) => {
      const up = list.find((u) => u.id === id);
      return sum + (up ? upgradeCost(up, unit, entry.quantity) : 0);
    }, 0);
    const taken = selectedUpgradeIds.filter((id) => list.some((u) => u.id === id)).length;
    // Families cap either the points spent or the number taken, never both.
    const overBudget =
      (allowance.max_points !== undefined && spent > allowance.max_points) ||
      (allowance.max_count !== undefined && taken > allowance.max_count);
    return [{ allowance, family, list, spent, taken, overBudget }];
  });

  const hasMountOption = options.some((o) => isMountPointerOption(o.description));
  const availableMounts = hasMountOption ? getAvailableMounts(unit, faction) : [];
  // Honour gating: a whitelist honour restricts mounts to its list; locked mounts need their honour selected
  const visibleMounts = selectedHonour?.cannot_be_mounted
    ? []
    : availableMounts.filter((m) => !selectedHonour?.mount_whitelist || selectedHonour.mount_whitelist.includes(m.id));
  const mountLockedBy = (m: Unit) =>
    m.requires_honour && entry.selectedVirtueId !== m.requires_honour
      ? honourList.find((h) => h.id === m.requires_honour)
      : undefined;
  const vowOptions = options.filter(
    (o) => o.max_points === undefined && o.description.startsWith('Replace') && o.description.includes('Vow')
  );
  // Whether the model is a Wizard - by profile, wizard upgrade, honour or the
  // Wizarding Hat. MUST be declared before regularOptions below: its filter
  // callback runs immediately, and reading a const before its declaration is a
  // ReferenceError that blanks the whole page for any unit carrying a
  // requires_wizard option (i.e. every wizard-capable character).
  const entryIsWizard = isWizardEntry(unit, entry, faction);
  const regularOptions = options.filter(
    (o) =>
      o.max_points === undefined &&
      !o.choices &&
      o.per_n_models === undefined &&
      // The upgrade-family allowance is rendered by its own picker below. Most
      // allowances carry max_points and are already excluded; count-capped ones
      // (Incantation Scrolls) are not, and would otherwise show as a bare checkbox.
      !upgradeAllowances.includes(o) &&
      !HONOUR_FAMILIES.some((f) => o.description.includes(f.match)) &&
      !isMountPointerOption(o.description) &&
      !(o.description.startsWith('Replace') && o.description.includes('Vow')) &&
      // Wizard-only options (Sorcerous Ruses) stay hidden until the model actually
      // is a Wizard — by profile, by a wizard upgrade, or via the Wizarding Hat.
      (!o.requires_wizard || entryIsWizard)
  );

  const scaledOptions = options.filter((o) => o.per_n_models !== undefined);

  const choiceGroups = options.filter(
    (o) =>
      o.max_points === undefined &&
      o.choices &&
      o.choices.length > 0
  );

  const command = unit.command ?? [];

  // Group regular options by category; uncategorised options fall into 'special'
  const weaponOptions = regularOptions.filter((o) => o.category === 'weapon');
  const armourOptions = regularOptions.filter((o) => o.category === 'armour');
  const specialOptions = regularOptions.filter((o) => !o.category || o.category === 'special');

  // Group choice groups by category
  const weaponChoiceGroups = choiceGroups.filter((o) => o.category === 'weapon');
  const armourChoiceGroups = choiceGroups.filter((o) => o.category === 'armour');
  const specialChoiceGroups = choiceGroups.filter((o) => !o.category || o.category === 'special');

  // Base (always-carried) equipment, split for display under the option sections
  const baseEquipList = flattenEquipment(unit.equipment);
  const baseArmourItems = baseEquipList.filter((e) => /armour|armor|shield|barding|pelt|plumage|scales/i.test(e));
  const baseWeaponItems = baseEquipList.filter((e) => !baseArmourItems.includes(e));

  const hasWeapons = weaponOptions.length > 0 || weaponChoiceGroups.length > 0 || baseWeaponItems.length > 0;
  const hasArmour = armourOptions.length > 0 || armourChoiceGroups.length > 0 || baseArmourItems.length > 0;
  const hasSpecial = specialOptions.length > 0 || scaledOptions.length > 0 || specialChoiceGroups.length > 0;

  // Free equipment granted by the selected honour, and whether a taken piece blocks other weapons
  const honourFreeEquipment = (selectedHonour?.free_equipment ?? []).map((fe) => ({
    ...fe,
    desc: honourEquipDesc(fe.name, selectedHonour!),
  }));
  const weaponsBlockedBy = honourFreeEquipment.find(
    (fe) => fe.blocks_weapons && entry.selectedOptions.includes(fe.desc)
  );
  // A Chivalrous Vow can forbid classes of magic items by name (Questing Vow: no lances,
  // magical or otherwise). The active vow is the vow-replacement option whose name appears
  // in a selected option description.
  const activeVow = (faction.chivalrous_vows ?? []).find((v) =>
    entry.selectedOptions.some((d) => d.includes(v.name))
  );
  const vowItemBan = activeVow?.forbids_item_name_match
    ? new RegExp(`\\b${activeVow.forbids_item_name_match}\\b`, 'i')
    : null;

  // Drop selections belonging to options blocked by a condition_not that is now selected,
  // by the honour's forbids_options, or by an honour weapon that excludes other weapons
  function applyExclusions(next: string[]): string[] {
    let out = next;
    for (const o of options) {
      if (o.condition_not && out.includes(o.condition_not)) {
        const own = [o.description, ...(o.choices ?? []).map((c) => c.description)];
        out = out.filter((d) => !own.includes(d));
      }
    }
    if (selectedHonour?.forbids_options) {
      out = out.filter((d) => !selectedHonour.forbids_options!.includes(d));
    }
    const blocker = honourFreeEquipment.find((fe) => fe.blocks_weapons && out.includes(fe.desc));
    if (blocker) {
      const weaponDescs = options.flatMap((o) => [
        ...(o.category === 'weapon' && !o.choices ? [o.description] : []),
        ...(o.choices ?? []).filter((c) => (c.category ?? o.category) === 'weapon').map((c) => c.description),
      ]);
      out = out.filter((d) => !weaponDescs.includes(d));
    }
    return out;
  }

  function toggleHonourEquip(desc: string, checked: boolean) {
    const next = checked
      ? [...entry.selectedOptions, desc]
      : entry.selectedOptions.filter((d) => d !== desc);
    updateEntry(armyId, entry.id, { selectedOptions: applyExclusions(next) });
  }

  // Entry-local prerequisites: mounted-only weapons and standard purchases.
  // Selected-but-now-invalid states are also caught by validateArmy.
  const hasMount = entryHasMount(entry);
  function unmetReqFor(desc: string): string | undefined {
    if (optionRequiresMount(desc) && !hasMount) return 'requires a mount';
    if (optionRequiresStandardBearer(desc) && !entry.includeStandard) return 'requires a standard bearer';
    return undefined;
  }

  // Honour-driven disable reasons for an option/group: forbidden outright, or a weapon
  // while an honour weapon that excludes other non-magical weapons is taken
  function externalBlockFor(opt: Option, isWeapon: boolean): string | undefined {
    if (selectedHonour?.forbids_options?.includes(opt.description)) return selectedHonour.name;
    if (isWeapon && weaponsBlockedBy) return weaponsBlockedBy.name;
    return undefined;
  }

  function toggleOption(desc: string, checked: boolean) {
    let next = checked
      ? [...entry.selectedOptions, desc]
      : entry.selectedOptions.filter((d) => d !== desc);
    // If unchecking an option, also remove any options that depend on it as a condition
    if (!checked) {
      const dependents = options.filter((o) => o.condition === desc).map((o) => o.description);
      if (dependents.length > 0) next = next.filter((d) => !dependents.includes(d));
    }
    const patch: Partial<Omit<import('../types/army').ArmyEntry, 'id'>> = { selectedOptions: applyExclusions(next) };
    // Clear BSB standard when BSB upgrade is removed
    if (!checked && desc === 'Upgrade to Battle Standard Bearer') patch.selectedBsbStandardId = null;
    updateEntry(armyId, entry.id, patch);
  }

  function selectChoice(choices: OptionChoice[], choiceDesc: string | null) {
    const allDescs = choices.map((c) => c.description);
    const without = entry.selectedOptions.filter((d) => !allDescs.includes(d));
    const next = choiceDesc ? [...without, choiceDesc] : without;
    updateEntry(armyId, entry.id, { selectedOptions: applyExclusions(next) });
  }

  function toggleChoice(choiceDesc: string) {
    const already = entry.selectedOptions.includes(choiceDesc);
    const next = already
      ? entry.selectedOptions.filter((d) => d !== choiceDesc)
      : [...entry.selectedOptions, choiceDesc];
    updateEntry(armyId, entry.id, { selectedOptions: next });
  }

  function selectVow(desc: string | null) {
    const without = entry.selectedOptions.filter((d) => !vowOptions.some((v) => v.description === d));
    const patch: Partial<Omit<import('../types/army').ArmyEntry, 'id'>> = {
      selectedOptions: applyExclusions(desc ? [...without, desc] : without),
    };
    // A vow can forbid magic items by name (Questing Vow: lances) - drop any already taken
    const newVow = desc ? (faction.chivalrous_vows ?? []).find((v) => desc.includes(v.name)) : undefined;
    if (newVow?.forbids_item_name_match) {
      const ban = new RegExp(`\\b${newVow.forbids_item_name_match}\\b`, 'i');
      const kept = (entry.selectedMagicItemIds ?? []).filter((id) => {
        const item = faction.magic_items.find((i) => i.id === id);
        return !item || !ban.test(item.name);
      });
      if (kept.length !== (entry.selectedMagicItemIds ?? []).length) patch.selectedMagicItemIds = kept;
    }
    updateEntry(armyId, entry.id, patch);
  }

  function selectHonour(id: string | null) {
    const honour = honourList.find((v) => v.id === id);
    const patch: Partial<Omit<import('../types/army').ArmyEntry, 'id'>> = { selectedVirtueId: id };
    // Mandate the honour's mount rules: clear a mount the new honour forbids or no longer unlocks
    const mount = availableMounts.find((m) => m.id === entry.selectedMountId);
    if (mount && (
      honour?.cannot_be_mounted ||
      (mount.requires_honour && mount.requires_honour !== id) ||
      (honour?.mount_whitelist && !honour.mount_whitelist.includes(mount.id))
    )) {
      patch.selectedMountId = null;
    }
    // Drop the previous honour's free equipment and anything the new honour forbids
    const staleEquip = allHonourEquipDescs(faction);
    let nextOptions = entry.selectedOptions.filter((d) => !staleEquip.has(d));
    if (honour?.forbids_options) {
      nextOptions = nextOptions.filter((d) => !honour.forbids_options!.includes(d));
    }
    if (nextOptions.length !== entry.selectedOptions.length) {
      patch.selectedOptions = nextOptions;
    }
    updateEntry(armyId, entry.id, patch);
  }

  function toggleUpgrade(upgradeId: string, checked: boolean) {
    const current = entry.selectedUpgradeIds ?? [];
    const next = checked
      ? [...current, upgradeId]
      : current.filter((id) => id !== upgradeId);
    updateEntry(armyId, entry.id, { selectedUpgradeIds: next });
  }

  /** Set how many copies of one upgrade this model has (for "0-2 per model" upgrades) */
  function setUpgradeCount(upgradeId: string, count: number) {
    const others = (entry.selectedUpgradeIds ?? []).filter((id) => id !== upgradeId);
    const next = [...others, ...Array<string>(Math.max(0, count)).fill(upgradeId)];
    updateEntry(armyId, entry.id, { selectedUpgradeIds: next });
  }

  function toggleMagicItem(itemId: string, checked: boolean) {
    const next = checked
      ? [...(entry.selectedMagicItemIds ?? []), itemId]
      : (entry.selectedMagicItemIds ?? []).filter((id) => id !== itemId);
    updateEntry(armyId, entry.id, { selectedMagicItemIds: next });
  }

  /**
   * Add or remove a single copy. Extremely common items have no limit on "how
   * many duplicates a character can carry" (rulebook p.338), so they are
   * counted rather than toggled.
   */
  function changeMagicItemCount(itemId: string, delta: number) {
    const cur = entry.selectedMagicItemIds ?? [];
    let next: string[];
    if (delta > 0) {
      next = [...cur, itemId];
    } else {
      const i = cur.indexOf(itemId);
      if (i === -1) return;
      next = [...cur.slice(0, i), ...cur.slice(i + 1)];
    }
    updateEntry(armyId, entry.id, { selectedMagicItemIds: next });
  }

  const selectedVow = vowOptions.find((v) => entry.selectedOptions.includes(v.description));
  const selectedItems = entry.selectedMagicItemIds ?? [];
  const selectedMount = availableMounts.find((m) => m.id === entry.selectedMountId);

  // Personal magic items for characters
  const characterItemCategories = ['magic_weapon', 'magic_armour', 'talisman', 'enchanted_item', 'arcane_item'] as const;
  // Personal items only: the unit's magic standard also lives in
  // selectedMagicItemIds but spends the standard-bearer's own budget, not the
  // champion's item allowance (player report, Hordes of Chaos Chosen).
  const totalItemPts = selectedItems.reduce((sum, id) => {
    const item = faction.magic_items.find((i) => i.id === id);
    return sum + (item && item.category !== 'magic_standard' ? item.points : 0);
  }, 0);
  const remainingAllowance = (magicAllowanceOpt?.max_points ?? 0) - totalItemPts;

  // Magic standard for units with standard bearer
  const standardCap = magicStandardCap(unit);
  const hasMagicStandardSlot = standardCap > 0 && entry.includeStandard;
  const hasBsbStandardSlot = entry.selectedOptions.includes('Upgrade to Battle Standard Bearer');
  const selectedStandardItem = hasMagicStandardSlot
    ? faction.magic_items.find((i) => i.category === 'magic_standard' && selectedItems.includes(i.id))
    : null;

  const divider = <div className="border-t" style={{ borderColor: 'var(--f-border)' }} />;

  return (
    <div className="mt-1.5 pt-2 flex flex-col gap-2.5 border-t" style={{ borderColor: 'var(--f-border)' }}>

      {/* Command */}
      {command.length > 0 && (
        <OptionsSection label="Command">
          {command.map((cmd, cmdIdx) => {
            const champRank = cmd.role === 'champion'
              ? command.filter(c => c.role === 'champion').indexOf(cmd)
              : -1;
            const checked = cmd.role === 'champion'
              ? champRank === 0
                ? entry.includeChampion
                : entry.selectedOptions.includes(cmd.name ?? '')
              : cmd.role === 'standard_bearer' ? entry.includeStandard
              : entry.includeMusician;
            const label = cmd.role === 'champion' ? (cmd.name ?? 'Champion')
              : cmd.role === 'standard_bearer' ? 'Standard Bearer'
              : (cmd.name ?? 'Musician');
            const onChange = (v: boolean) => {
              if (cmd.role === 'champion') {
                if (champRank === 0) {
                  updateEntry(armyId, entry.id, { includeChampion: v });
                } else {
                  const name = cmd.name ?? '';
                  let next = v
                    ? [...entry.selectedOptions, name]
                    : entry.selectedOptions.filter(o => o !== name);
                  // If unchecking, remove any options conditioned on this champion
                  if (!v) {
                    const champCondition = `${name} champion only`;
                    const conditioned = options.filter(o => o.condition === champCondition).map(o => o.description);
                    if (conditioned.length > 0) next = next.filter(d => !conditioned.includes(d));
                  }
                  updateEntry(armyId, entry.id, { selectedOptions: next });
                }
              } else if (cmd.role === 'standard_bearer') {
                updateEntry(armyId, entry.id, { includeStandard: v });
              } else {
                updateEntry(armyId, entry.id, { includeMusician: v });
              }
            };
            return <OptionCheckbox key={`${cmd.role}-${cmdIdx}`} label={`${label} - +${cmd.cost_per_unit} pts`} checked={checked} onChange={onChange} />;
          })}
        </OptionsSection>
      )}

      {/* Weapons */}
      {hasWeapons && (
        <OptionsSection label="Weapons">
          {baseWeaponItems.length > 0 && (
            <p className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.75 }}>
              Includes: {baseWeaponItems.join(', ')}
            </p>
          )}
          {weaponOptions.map((opt) => (
            <RegularOption key={opt.description} opt={opt} entry={entry} toggleOption={toggleOption} externalBlock={externalBlockFor(opt, true)} unmetReq={unmetReqFor(opt.description)} />
          ))}
          {weaponChoiceGroups.map((group, gi) => (
            <ChoiceGroup key={`${group.description}-${gi}`} group={group} entry={entry} selectChoice={selectChoice} toggleChoice={toggleChoice} armyId={armyId} externalBlock={externalBlockFor(group, true)} choiceBlock={unmetReqFor} />
          ))}
        </OptionsSection>
      )}

      {/* Armour & Shields */}
      {hasArmour && (
        <OptionsSection label="Armour &amp; Shields">
          {baseArmourItems.length > 0 && (
            <p className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.75 }}>
              Includes: {baseArmourItems.join(', ')}
            </p>
          )}
          {armourOptions.map((opt) => (
            <RegularOption key={opt.description} opt={opt} entry={entry} toggleOption={toggleOption} externalBlock={externalBlockFor(opt, false)} unmetReq={unmetReqFor(opt.description)} />
          ))}
          {armourChoiceGroups.map((group, gi) => (
            <ChoiceGroup key={`${group.description}-${gi}`} group={group} entry={entry} selectChoice={selectChoice} toggleChoice={toggleChoice} armyId={armyId} externalBlock={externalBlockFor(group, false)} choiceBlock={unmetReqFor} />
          ))}
        </OptionsSection>
      )}

      {/* Special Upgrades */}
      {hasSpecial && (
        <OptionsSection label="Special Upgrades">
          {specialOptions.map((opt) => (
            <RegularOption key={opt.description} opt={opt} entry={entry} toggleOption={toggleOption} externalBlock={externalBlockFor(opt, false)} unmetReq={unmetReqFor(opt.description)} />
          ))}
          {scaledOptions.map((opt) => {
            const maxAllowed = Math.min(Math.floor(entry.quantity / opt.per_n_models!), opt.max_count ?? 99);
            const qty = entry.optionQuantities?.[opt.description] ?? 0;
            const totalCost = opt.points * qty;
            function setQty(next: number) {
              updateEntry(armyId, entry.id, {
                optionQuantities: { ...(entry.optionQuantities ?? {}), [opt.description]: next },
              });
            }
            return (
              <div key={opt.description} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(Math.max(0, qty - 1))} disabled={qty <= 0} className="w-6 h-6 rounded text-sm font-bold disabled:opacity-30" style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text)' }}>−</button>
                  <span className="text-sm font-semibold w-4 text-center" style={{ color: 'var(--f-text)' }}>{qty}</span>
                  <button onClick={() => setQty(Math.min(maxAllowed, qty + 1))} disabled={qty >= maxAllowed} className="w-6 h-6 rounded text-sm font-bold disabled:opacity-30" style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text)' }}>+</button>
                  <span className="text-xs" style={{ color: qty > 0 ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                    {opt.description}{totalCost > 0 ? ` - +${totalCost} pts` : ''}
                  </span>
                  <span className="text-xs ml-auto" style={{ color: 'var(--f-text-3)' }}>max {maxAllowed}</span>
                </div>
                {opt.notes && <span className="text-xs italic ml-8" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>{opt.notes}</span>}
              </div>
            );
          })}
          {specialChoiceGroups.map((group, gi) => (
            <ChoiceGroup key={`${group.description}-${gi}`} group={group} entry={entry} selectChoice={selectChoice} toggleChoice={toggleChoice} armyId={armyId} externalBlock={externalBlockFor(group, false)} choiceBlock={unmetReqFor} />
          ))}
        </OptionsSection>
      )}

      {/* Vow */}
      {vowOptions.length > 0 && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>Vow</p>
            <div className="flex flex-col gap-1">
              {/* Default vow */}
              <label className="flex items-center gap-2 cursor-pointer">
                <span
                  className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                  style={{
                    borderColor: !selectedVow ? 'var(--f-primary)' : 'var(--f-border)',
                    backgroundColor: !selectedVow ? 'var(--f-primary)' : 'transparent',
                  }}
                >
                  {!selectedVow && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <input type="radio" name={`vow-${entry.id}`} checked={!selectedVow} onChange={() => selectVow(null)} className="sr-only" />
                <span className="text-xs" style={{ color: !selectedVow ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                  Knight's Vow
                </span>
              </label>
              {vowOptions.map((opt) => {
                const isActive = selectedVow?.description === opt.description;
                const vowName = opt.description.replace(/^.+?with the /, '');
                return (
                  <label key={opt.description} className="flex items-center gap-2 cursor-pointer">
                    <span
                      className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                      style={{
                        borderColor: isActive ? 'var(--f-primary)' : 'var(--f-border)',
                        backgroundColor: isActive ? 'var(--f-primary)' : 'transparent',
                      }}
                    >
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <input type="radio" name={`vow-${entry.id}`} checked={isActive} onChange={() => selectVow(opt.description)} className="sr-only" />
                    <span className="text-xs" style={{ color: isActive ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                      {vowName} - +{opt.points} pts
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Mount picker */}
      {(visibleMounts.length > 0 || (availableMounts.length > 0 && selectedHonour?.cannot_be_mounted)) && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>Mount</p>
            {selectedHonour?.cannot_be_mounted && (
              <p className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                Cannot be mounted ({selectedHonour.name})
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <label className={`flex items-center gap-2 ${selectedHonour?.must_be_mounted ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
                <span
                  className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                  style={{
                    borderColor: !entry.selectedMountId ? 'var(--f-primary)' : 'var(--f-border)',
                    backgroundColor: !entry.selectedMountId ? 'var(--f-primary)' : 'transparent',
                  }}
                >
                  {!entry.selectedMountId && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <input type="radio" name={`mount-${entry.id}`} checked={!entry.selectedMountId} disabled={!!selectedHonour?.must_be_mounted} onChange={() => !selectedHonour?.must_be_mounted && updateEntry(armyId, entry.id, { selectedMountId: null })} className="sr-only" />
                <span className="text-xs" style={{ color: !entry.selectedMountId ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                  On foot{selectedHonour?.must_be_mounted ? ` - must be mounted (${selectedHonour.name})` : ''}
                </span>
              </label>
              {visibleMounts.map((mount) => {
                const isActive = entry.selectedMountId === mount.id;
                const lockedBy = mountLockedBy(mount);
                const mp = mount.profiles[0]?.profile;
                return (
                  <div key={mount.id} className={`flex flex-col gap-0.5${lockedBy ? ' opacity-40' : ''}`}>
                    <label className={`flex items-start gap-2 ${lockedBy ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <span
                        className="shrink-0 w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor: isActive ? 'var(--f-primary)' : 'var(--f-border)',
                          backgroundColor: isActive ? 'var(--f-primary)' : 'transparent',
                        }}
                      >
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                      <input type="radio" name={`mount-${entry.id}`} checked={isActive} disabled={!!lockedBy} onChange={() => !lockedBy && updateEntry(armyId, entry.id, { selectedMountId: mount.id })} className="sr-only" />
                      <span className="flex flex-col min-w-0 gap-0.5">
                        <span className="text-xs leading-snug" style={{ color: isActive ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                          {mount.name} - +{mount.points} pts{lockedBy ? ` - requires ${lockedBy.name}` : ''}
                        </span>
                        {mp && (
                          <span className="text-xs" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                            M{mp.M} WS{mp.WS} S{mp.S} T{String(mp.T) === '-' ? '—' : mp.T} W{String(mp.W) === '-' ? '—' : mp.W} I{mp.I} A{mp.A}
                          </span>
                        )}
                        {mount.profiles[0]?.mount_grants && (
                          <span className="text-xs italic" style={{ color: 'var(--f-primary)', opacity: 0.8 }}>
                            Grants: {mount.profiles[0].mount_grants}
                          </span>
                        )}
                      </span>
                    </label>
                    {isActive && mount.weapon_profiles && mount.weapon_profiles.length > 0 && (
                      <div className="ml-6 mt-1 overflow-x-auto">
                        <table className="text-xs w-full" style={{ borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                              <th className="text-left pr-2 pb-0.5 font-medium whitespace-nowrap">Weapon</th>
                              <th className="text-left pr-2 pb-0.5 font-medium whitespace-nowrap">Rng</th>
                              <th className="text-center pr-2 pb-0.5 font-medium">S</th>
                              <th className="text-center pr-2 pb-0.5 font-medium">AP</th>
                              <th className="text-left pb-0.5 font-medium">Special</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mount.weapon_profiles.map((wp) => (
                              <tr key={wp.name}>
                                <td className="text-left pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)', fontSize: '0.65rem' }}>{wp.name}</td>
                                <td className="text-left pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)' }}>{wp.range}</td>
                                <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{wp.S}</td>
                                <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{wp.AP}</td>
                                <td className="py-0.5" style={{ color: 'var(--f-text-3)' }}>
                                  <span className="text-xs">{wp.special_rules?.map(formatRuleName).join(', ') || '—'}</span>
                                  {wp.notes && <span className="text-xs italic opacity-70 ml-1"> - {wp.notes}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedMount?.notes && selectedMount.notes.filter(n => !n.startsWith('Available')).map((note, i) => (
              <p key={i} className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>{note}</p>
            ))}
          </div>
        </>
      )}

      {/* Honour-family picker (Knightly Virtue, Elven Honour, …) */}
      {honourFamily && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>{honourFamily.label}</p>
            <select
              value={entry.selectedVirtueId ?? ''}
              onChange={(e) => selectHonour(e.target.value || null)}
              className="text-xs rounded px-2 py-1"
              style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text)', border: '1px solid var(--f-border)' }}
            >
              <option value="">None</option>
              {honourList
                // Structured eligibility, same as the budgeted-upgrade picker.
                // (Substring-matching the prose for the unit's name broke when
                // the FoF FAQ v1.5.2 text was appended to every restriction.)
                .filter((v) => upgradeEligibility(v, unit, entry, faction).eligible)
                .map((v) => (
                  <option key={v.id} value={v.id}>{v.name} ({v.points} pts)</option>
                ))}

            </select>
            {selectedHonour && (
              <p className="text-xs italic leading-snug" style={{ color: 'var(--f-text-3)' }}>{selectedHonour.description}</p>
            )}
            {honourFreeEquipment.map((fe) => {
              const checked = entry.selectedOptions.includes(fe.desc);
              return (
                <label key={fe.desc} className="flex items-center gap-2 cursor-pointer ml-2">
                  <span
                    className="shrink-0 w-4 h-4 rounded border flex items-center justify-center text-xs"
                    style={{
                      borderColor: checked ? 'var(--f-gold)' : 'var(--f-border)',
                      backgroundColor: checked ? 'var(--f-gold)' : 'transparent',
                      color: '#0f1117',
                    }}
                  >
                    {checked ? '✓' : ''}
                  </span>
                  <input type="checkbox" checked={checked} onChange={(e) => toggleHonourEquip(fe.desc, e.target.checked)} className="sr-only" />
                  <span className="text-xs" style={{ color: checked ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                    {fe.name} - free{fe.blocks_weapons ? ' (no other non-magical weapons)' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}

      {/* Budgeted upgrade family (Gifts of Chaos, Vampiric Powers, Incantation Scrolls, …).
          Multi-select: a model buys as many as its allowance affords. Restrictions are
          free text and often depend on the model's Mark or troop type, so every upgrade
          in the family is listed with its restriction shown rather than hidden — the
          army validator reports anything illegal. */}
      {upgradeSections.map(({ allowance, family, list, spent, taken: familyTaken, overBudget }) => (
        <React.Fragment key={family.key}>
          {divider}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>
                {family.label}
              </p>
              <p
                className="text-xs font-semibold"
                style={{ color: overBudget ? 'var(--f-danger, #f87171)' : 'var(--f-text-3)' }}
              >
                {allowance.max_count !== undefined
                  ? `${familyTaken} / ${allowance.max_count} taken`
                  : `${spent} / ${allowance.max_points} pts`}
              </p>
            </div>
            {allowance.notes && (
              <p className="text-xs italic leading-snug" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                {allowance.notes}
              </p>
            )}
            {list.map((up) => {
              const taken = selectedUpgradeIds.filter((id) => id === up.id).length;
              const cap = maxPerModel(up);
              const { eligible, reason } = upgradeEligibility(up, unit, entry, faction);
              // An upgrade already taken stays interactive even if it has become
              // ineligible, so the player can always take it back off.
              const locked = !eligible && taken === 0;
              const takenElsewhere = isOncePerArmy(up) && taken === 0 &&
                allEntries.some((e) => e.id !== entry.id && (e.selectedUpgradeIds ?? []).includes(up.id));
              const disabled = locked || takenElsewhere;
              const note = takenElsewhere ? 'Already taken - once per army' : reason;

              const label = (
                <span className="flex flex-col">
                  <span
                    className="text-xs"
                    style={{ color: taken > 0 ? 'var(--f-text)' : 'var(--f-text-3)', opacity: disabled ? 0.45 : 1 }}
                  >
                    {up.name} ({upgradeCost(up, unit, entry.quantity)} pts
                    {unit.category !== 'character' && up.unit_points !== undefined && up.unit_points_per_model ? `, ${up.unit_points}/model` : ''})
                    {cap > 1 ? ` - 0-${cap} per model` : ''}
                  </span>
                  {note && (
                    <span className="text-xs italic leading-snug" style={{ color: 'var(--f-amber, #d97706)' }}>
                      {note}
                    </span>
                  )}
                  {!note && up.restrictions && (
                    <span className="text-xs italic leading-snug" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                      {up.restrictions}
                    </span>
                  )}
                </span>
              );

              // Upgrades allowing more than one per model get a stepper rather
              // than a checkbox, so the second copy is reachable.
              if (cap > 1) {
                return (
                  <div key={up.id} className="flex items-start gap-2">
                    <span className="flex items-center gap-1 shrink-0 mt-0.5">
                      <button
                        type="button"
                        aria-label={`Remove ${up.name}`}
                        disabled={taken === 0}
                        onClick={() => setUpgradeCount(up.id, taken - 1)}
                        className="w-5 h-5 rounded border text-xs leading-none"
                        style={{ borderColor: 'var(--f-border)', color: 'var(--f-text-3)', opacity: taken === 0 ? 0.4 : 1 }}
                      >
                        −
                      </button>
                      <span className="text-xs w-3 text-center" style={{ color: 'var(--f-text)' }}>{taken}</span>
                      <button
                        type="button"
                        aria-label={`Add ${up.name}`}
                        disabled={disabled || taken >= cap}
                        onClick={() => setUpgradeCount(up.id, taken + 1)}
                        className="w-5 h-5 rounded border text-xs leading-none"
                        style={{ borderColor: 'var(--f-border)', color: 'var(--f-text-3)', opacity: disabled || taken >= cap ? 0.4 : 1 }}
                      >
                        +
                      </button>
                    </span>
                    {label}
                  </div>
                );
              }

              return (
                <label
                  key={up.id}
                  className={`flex items-start gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className="shrink-0 w-4 h-4 mt-0.5 rounded border flex items-center justify-center text-xs"
                    style={{
                      borderColor: taken > 0 ? 'var(--f-gold)' : 'var(--f-border)',
                      backgroundColor: taken > 0 ? 'var(--f-gold)' : 'transparent',
                      color: '#0f1117',
                      opacity: disabled ? 0.4 : 1,
                    }}
                  >
                    {taken > 0 ? '✓' : ''}
                  </span>
                  <input
                    type="checkbox"
                    checked={taken > 0}
                    disabled={disabled}
                    onChange={(e) => toggleUpgrade(up.id, e.target.checked)}
                    className="sr-only"
                  />
                  {label}
                </label>
              );
            })}
          </div>
        </React.Fragment>
      ))}

      {/* Personal magic items (characters and unit champions) */}
      {magicAllowanceOpt && (!unit.command?.some((c) => c.role === 'champion') || entry.includeChampion) && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <button
              className="flex items-center justify-between w-full text-left"
              onClick={() => setExpandedMagicCategories({ ...expandedMagicCategories, magic_items_section: !(expandedMagicCategories.magic_items_section ?? false) })}
            >
              <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>
                Magic Items {!(expandedMagicCategories.magic_items_section ?? false) ? '▸' : '▾'}
              </p>
              <p className="text-xs" style={{ color: totalItemPts > (magicAllowanceOpt.max_points ?? 0) ? 'var(--f-gold)' : 'var(--f-text-3)' }}>
                {totalItemPts} / {magicAllowanceOpt.max_points} pts
              </p>
            </button>
            {(expandedMagicCategories.magic_items_section ?? false) && !entryIsWizard && (
              <p className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>Arcane items require a Wizard</p>
            )}
            {(expandedMagicCategories.magic_items_section ?? false) && characterItemCategories.map((cat) => {
              // Filter items available to this unit (check unit_restriction text against unit name/troop type)
              const allCatItems = availableMagicItems
                .filter((i) => i.category === cat)
                // An item costing more than the model's whole allowance can
                // never be taken - don't show it at all. (Items within the
                // allowance but over the REMAINING budget stay visible, with
                // the running total going red.)
                .filter((i) => (i.points ?? 0) <= (magicAllowanceOpt?.max_points ?? Infinity))
                // Rulebook p.341: magic armour needs the mundane equivalent (or
                // an item-level waiver), so a Wizard with no armour option never
                // sees suits it cannot take. A selected honour's free equipment
                // (Blood of Caledor's full plate) opens the armoury too.
                .filter((i) => magicArmourAllowed(i, unit, (selectedHonour?.free_equipment ?? []).map((fe) => fe.name)))
                .sort((x, y) => (y.points ?? 0) - (x.points ?? 0));
              const items = allCatItems.filter((i) => isMagicItemEligible(i.restrictions, unit, faction.units));
              if (items.length === 0) return null;
              if (cat === 'arcane_item' && !entryIsWizard) return null;
              const isExpanded = expandedMagicCategories[cat] ?? false;

              // For magic_armour: split into armour suits vs shields
              const armourItems = cat === 'magic_armour' ? items.filter((i) => !(i as typeof i & { is_shield?: boolean }).is_shield) : null;
              const shieldItems = cat === 'magic_armour' ? items.filter((i) => !!(i as typeof i & { is_shield?: boolean }).is_shield) : null;

              const renderItem = (item: typeof items[0], inputType: 'checkbox' | 'radio' = 'checkbox', radioName?: string) => {
                const itemCount = selectedItems.filter((id) => id === item.id).length;
                const isSelected = itemCount > 0;
                // Extremely common items "can be purchased alongside other magic items from the
                // same category" (rulebook p.338), so they neither block nor are blocked.
                const blockingSelection = selectedItems.find((id) => {
                  const sel = items.find((i) => i.id === id);
                  return sel != null && !(sel as { extremely_common?: boolean }).extremely_common;
                });
                const wouldExceed = item.points > remainingAllowance + (isSelected && !(item as { extremely_common?: boolean }).extremely_common ? item.points : 0);
                const categoryBlocked = inputType === 'checkbox' && !isSelected
                  && !(item as { extremely_common?: boolean }).extremely_common
                  && !!blockingSelection;
                const vowBlocked = vowItemBan != null && vowItemBan.test(item.name);
                const itemExt = item as typeof item & { single_use?: boolean; is_shield?: boolean; grants_rules?: string[]; extremely_common?: boolean };
                const isDisabled = (wouldExceed && !itemExt.extremely_common) || categoryBlocked || vowBlocked;
                return (
                  <label
                    key={item.id}
                    className={`flex items-start gap-2 pt-1.5 border-t ${isDisabled ? 'opacity-40' : 'cursor-pointer'}`}
                    style={{ borderColor: 'var(--f-border)' }}
                  >
                    {itemExt.extremely_common ? (
                      <span className="shrink-0 flex items-center gap-1 mt-0.5">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); changeMagicItemCount(item.id, -1); }}
                          disabled={itemCount === 0}
                          className="w-4 h-4 rounded border text-xs leading-none flex items-center justify-center"
                          style={{ borderColor: 'var(--f-border)', color: 'var(--f-text-3)', opacity: itemCount === 0 ? 0.4 : 1 }}
                          aria-label={`Remove one ${item.name}`}
                        >-</button>
                        <span className="text-xs w-3 text-center" style={{ color: isSelected ? 'var(--f-gold)' : 'var(--f-text-3)' }}>{itemCount}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); changeMagicItemCount(item.id, 1); }}
                          disabled={wouldExceed}
                          className="w-4 h-4 rounded border text-xs leading-none flex items-center justify-center"
                          style={{ borderColor: 'var(--f-border)', color: 'var(--f-text-3)', opacity: wouldExceed ? 0.4 : 1 }}
                          aria-label={`Add one ${item.name}`}
                        >+</button>
                      </span>
                    ) : inputType === 'checkbox' ? (
                      <span
                        className="shrink-0 w-4 h-4 mt-0.5 rounded border flex items-center justify-center text-xs"
                        style={{
                          borderColor: isSelected ? 'var(--f-gold)' : 'var(--f-border)',
                          backgroundColor: isSelected ? 'var(--f-gold)' : 'transparent',
                          color: '#0f1117',
                        }}
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                    ) : (
                      <span
                        className="shrink-0 w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor: isSelected ? 'var(--f-gold)' : 'var(--f-border)',
                          backgroundColor: isSelected ? 'var(--f-gold)' : 'transparent',
                        }}
                      >
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#0f1117' }} />}
                      </span>
                    )}
                    {itemExt.extremely_common ? null : (
                      <input
                        type={inputType}
                        name={radioName}
                        disabled={isDisabled}
                        checked={isSelected}
                        onChange={(e) => toggleMagicItem(item.id, e.target.checked)}
                        className="sr-only"
                      />
                    )}
                    <span className="flex flex-col min-w-0 gap-0.5 flex-1">
                      {/* Name + pts + badges */}
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium leading-snug" style={{ color: isSelected ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                          {item.name} - {item.points} pts{vowBlocked ? ` - not with the ${activeVow!.name}` : ''}
                        </span>
                        <ErrataIndicator errata={item.errata} />
                        {itemExt.single_use && (
                          <span className="text-xs px-1 rounded" style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text-3)', fontSize: '0.6rem' }}>Single Use</span>
                        )}
                      </span>
                      {/* Rules text */}
                      {(item as typeof item & { rules_text?: string }).rules_text && (
                        <span className="text-xs leading-snug" style={{ color: 'var(--f-text-3)' }}>
                          {(item as typeof item & { rules_text?: string }).rules_text}
                        </span>
                      )}
                      {/* Weapon profile */}
                      {item.weapon_profile && (
                        <div className="mt-0.5 overflow-x-auto">
                          <table className="text-xs w-full" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                                <th className="text-left pr-2 pb-0.5 font-medium whitespace-nowrap">Rng</th>
                                <th className="text-center pr-2 pb-0.5 font-medium">S</th>
                                <th className="text-center pr-2 pb-0.5 font-medium">AP</th>
                                <th className="text-left pb-0.5 font-medium">Special</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)' }}>{item.weapon_profile.range}</td>
                                <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{item.weapon_profile.S}</td>
                                <td className="text-center pr-2 py-0.5" style={{ color: 'var(--f-text)' }}>{item.weapon_profile.AP}</td>
                                <td className="py-0.5" style={{ color: 'var(--f-text-3)' }}>{item.weapon_profile.special_rules?.map(formatRuleName).join(', ') || '—'}</td>
                              </tr>
                            </tbody>
                          </table>
                          {item.weapon_profile.notes && (
                            <p className="text-xs italic mt-0.5" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>{item.weapon_profile.notes}</p>
                          )}
                        </div>
                      )}
                      {/* Armour profile */}
                      {item.armour_profile && (
                        <div className="mt-0.5 overflow-x-auto">
                          <table className="text-xs w-full" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                                <th className="text-left pr-2 pb-0.5 font-medium whitespace-nowrap">Armour</th>
                                <th className="text-left pb-0.5 font-medium">Special</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="pr-2 py-0.5 whitespace-nowrap" style={{ color: 'var(--f-text)' }}>{item.armour_profile.armour_value}</td>
                                <td className="py-0.5" style={{ color: 'var(--f-text-3)' }}>{item.armour_profile.special_rules?.map(formatRuleName).join(', ') || '—'}</td>
                              </tr>
                            </tbody>
                          </table>
                          {item.armour_profile.notes && (
                            <p className="text-xs italic mt-0.5" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>{item.armour_profile.notes}</p>
                          )}
                        </div>
                      )}
                    </span>
                  </label>
                );
              };

              return (
                <div key={cat} className="flex flex-col gap-0">
                  <button
                    onClick={() => setExpandedMagicCategories({ ...expandedMagicCategories, [cat]: !expandedMagicCategories[cat] })}
                    className="text-xs uppercase tracking-wide text-left py-1 px-1.5 rounded hover:opacity-80 transition-opacity"
                    style={{ color: 'var(--f-text-3)', opacity: 0.6, fontSize: '0.65rem' }}
                  >
                    {isExpanded ? '▼' : '▶'} {MAGIC_ITEM_CATEGORY_LABELS[cat]}
                  </button>
                  {isExpanded && cat === 'magic_armour' ? (
                    <>
                      {armourItems && armourItems.length > 0 && (
                        <div className="flex flex-col">
                          <p className="text-xs px-1 mb-0.5" style={{ color: 'var(--f-text-3)', opacity: 0.5, fontSize: '0.6rem' }}>ARMOUR</p>
                          {armourItems.map((item) => renderItem(item))}
                        </div>
                      )}
                      {shieldItems && shieldItems.length > 0 && (
                        <div className="flex flex-col mt-1.5">
                          <p className="text-xs px-1 mb-0.5" style={{ color: 'var(--f-text-3)', opacity: 0.5, fontSize: '0.6rem' }}>SHIELDS</p>
                          {shieldItems.map((item) => renderItem(item))}
                        </div>
                      )}
                    </>
                  ) : isExpanded ? (
                    items.map((item) => renderItem(item))
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* BSB Magic Standard (unlimited cost, separate from personal allowance) */}
      {hasBsbStandardSlot && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <button
              className="flex items-center justify-between w-full text-left"
              onClick={() => setExpandedMagicCategories({ ...expandedMagicCategories, bsb_standard: !expandedMagicCategories.bsb_standard })}
            >
              <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>
                Battle Standard - Magic Standard {expandedMagicCategories.bsb_standard ? '▾' : '▸'}
                {!expandedMagicCategories.bsb_standard && entry.selectedBsbStandardId && (() => {
                  const std = faction.magic_items.find((i) => i.id === entry.selectedBsbStandardId);
                  return std ? <span className="font-normal ml-1" style={{ color: 'var(--f-text)' }}>- {std.name}</span> : null;
                })()}
              </p>
              <p className="text-xs" style={{ color: 'var(--f-text-3)' }}>
                {entry.selectedBsbStandardId
                  ? `${faction.magic_items.find((i) => i.id === entry.selectedBsbStandardId)?.points ?? 0} pts`
                  : 'No limit'}
              </p>
            </button>
            {expandedMagicCategories.bsb_standard && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span
                    className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                    style={{
                      borderColor: !entry.selectedBsbStandardId ? 'var(--f-primary)' : 'var(--f-border)',
                      backgroundColor: !entry.selectedBsbStandardId ? 'var(--f-primary)' : 'transparent',
                    }}
                  >
                    {!entry.selectedBsbStandardId && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>
                  <input type="radio" name={`bsb-std-${entry.id}`} checked={!entry.selectedBsbStandardId} onChange={() => updateEntry(armyId, entry.id, { selectedBsbStandardId: null })} className="sr-only" />
                  <span className="text-xs" style={{ color: !entry.selectedBsbStandardId ? 'var(--f-text)' : 'var(--f-text-3)' }}>No magic standard</span>
                </label>
                {availableMagicItems
                  .filter((i) => i.category === 'magic_standard')
                  .sort((x, y) => (y.points ?? 0) - (x.points ?? 0))
                  .map((item) => {
                    const isSelected = entry.selectedBsbStandardId === item.id;
                    return (
                      <label key={item.id} className="flex items-start gap-2 pt-1.5 border-t cursor-pointer" style={{ borderColor: 'var(--f-border)' }}>
                        <span
                          className="shrink-0 w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center"
                          style={{
                            borderColor: isSelected ? 'var(--f-gold)' : 'var(--f-border)',
                            backgroundColor: isSelected ? 'var(--f-gold)' : 'transparent',
                          }}
                        >
                          {isSelected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#0f1117' }} />}
                        </span>
                        <input
                          type="radio"
                          name={`bsb-std-${entry.id}`}
                          checked={isSelected}
                          onChange={() => updateEntry(armyId, entry.id, { selectedBsbStandardId: item.id })}
                          className="sr-only"
                        />
                        <span className="flex flex-col min-w-0 gap-0.5">
                          <span className="text-xs font-medium leading-snug" style={{ color: isSelected ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                            {item.name} - {item.points} pts
                          </span>
                          {(item as typeof item & { rules_text?: string }).rules_text && (
                            <span className="text-xs leading-snug" style={{ color: 'var(--f-text-3)' }}>
                              {(item as typeof item & { rules_text?: string }).rules_text}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
              </>
            )}
          </div>
        </>
      )}

      {/* Magic Standard (units with standard bearer) */}
      {hasMagicStandardSlot && (
        <>
          {divider}
          <div className="flex flex-col gap-1.5">
            <button
              className="flex items-center justify-between w-full text-left"
              onClick={() => setExpandedMagicCategories({ ...expandedMagicCategories, magic_standard: !expandedMagicCategories.magic_standard })}
            >
              <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>
                Magic Standard {expandedMagicCategories.magic_standard ? '▾' : '▸'}
                {!expandedMagicCategories.magic_standard && selectedStandardItem && (
                  <span className="font-normal ml-1" style={{ color: 'var(--f-text)' }}>- {selectedStandardItem.name}</span>
                )}
              </p>
              <p className="text-xs" style={{ color: 'var(--f-text-3)' }}>
                {selectedStandardItem ? `${selectedStandardItem.points}` : '0'} / {standardCap} pts
              </p>
            </button>
            {expandedMagicCategories.magic_standard && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span
                    className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                    style={{
                      borderColor: !selectedStandardItem ? 'var(--f-primary)' : 'var(--f-border)',
                      backgroundColor: !selectedStandardItem ? 'var(--f-primary)' : 'transparent',
                    }}
                  >
                    {!selectedStandardItem && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>
                  <input
                    type="radio"
                    name={`std-${entry.id}`}
                    checked={!selectedStandardItem}
                    onChange={() => {
                      const without = selectedItems.filter((id) => !faction.magic_items.find((i) => i.id === id && i.category === 'magic_standard'));
                      updateEntry(armyId, entry.id, { selectedMagicItemIds: without });
                    }}
                    className="sr-only"
                  />
                  <span className="text-xs" style={{ color: !selectedStandardItem ? 'var(--f-text)' : 'var(--f-text-3)' }}>No magic standard</span>
                </label>
                {availableMagicItems
                  .filter((i) => i.category === 'magic_standard' && i.points <= standardCap)
                  .sort((x, y) => (y.points ?? 0) - (x.points ?? 0))
                  .map((item) => {
                    const isSelected = selectedItems.includes(item.id);
                    return (
                      <label key={item.id} className="flex items-start gap-2 pt-1.5 border-t cursor-pointer" style={{ borderColor: 'var(--f-border)' }}>
                        <span
                          className="shrink-0 w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center"
                          style={{
                            borderColor: isSelected ? 'var(--f-gold)' : 'var(--f-border)',
                            backgroundColor: isSelected ? 'var(--f-gold)' : 'transparent',
                          }}
                        >
                          {isSelected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#0f1117' }} />}
                        </span>
                        <input
                          type="radio"
                          name={`std-${entry.id}`}
                          checked={isSelected}
                          onChange={() => {
                            const without = selectedItems.filter((id) => !faction.magic_items.find((i) => i.id === id && i.category === 'magic_standard'));
                            updateEntry(armyId, entry.id, { selectedMagicItemIds: [...without, item.id] });
                          }}
                          className="sr-only"
                        />
                        <span className="flex flex-col min-w-0 gap-0.5">
                          <span className="text-xs font-medium leading-snug" style={{ color: isSelected ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                            {item.name} - {item.points} pts
                          </span>
                          {(item as typeof item & { rules_text?: string }).rules_text && (
                            <span className="text-xs leading-snug" style={{ color: 'var(--f-text-3)' }}>
                              {(item as typeof item & { rules_text?: string }).rules_text}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
              </>
            )}
          </div>
        </>
      )}

      {/* Runic Items (Dwarfs only) */}
      {faction.runic_items && (faction.runic_items.length > 0) && (
        <RunicItemsPicker
          unit={unit}
          entry={entry}
          faction={faction}
          armyId={armyId}
          allEntries={allEntries}
          updateEntry={updateEntry}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Runic Items Picker (Dwarfs only)
// ---------------------------------------------------------------------------

const EMPTY_RUNIC: RunicItemState = {
  weaponSlot: undefined,
  weaponRunes: [],
  armourRunes: [],
  talismanRunes: [],
  engineeringRunes: [],
  tattooRunes: [],
};

const WEAPON_SLOT_LABELS: Record<string, string> = {
  hand_weapon: 'Hand Weapon',
  great_weapon: 'Great Weapon',
  crossbow: 'Crossbow',
  handgun: 'Handgun',
};

/** Units that can take tattoos, and their limits */
const TATTOO_LIMITS: Record<string, number> = {
  doomseekers: 3,
  daemon_slayer: 2,
  dragon_slayer: 1,
};

type RuneListKey = 'weaponRunes' | 'armourRunes' | 'talismanRunes' | 'engineeringRunes' | 'tattooRunes';

function RunicItemsPicker({
  unit,
  entry,
  faction,
  armyId,
  allEntries,
  updateEntry,
}: {
  unit: Unit;
  entry: ArmyEntry;
  faction: Faction;
  armyId: string;
  allEntries: ArmyEntry[];
  updateEntry: (armyId: string, entryId: string, patch: Partial<Omit<ArmyEntry, 'id'>>) => void;
}) {
  const allRunes = faction.runic_items ?? [];
  const ri: RunicItemState = entry.runicItems ?? { ...EMPTY_RUNIC, weaponRunes: [], armourRunes: [], talismanRunes: [], engineeringRunes: [], tattooRunes: [] };
  const isWarMachine = unit.category === 'war_machine';
  const tattooLimit = TATTOO_LIMITS[unit.id] ?? 0;
  const hasTattoos = tattooLimit > 0;

  // Points allowance - from the "May purchase Runic Items" option
  const runicAllowanceOpt = (unit.options ?? []).find(
    (o) => o.max_points !== undefined && (
      o.description.toLowerCase().includes('runic') ||
      o.description.toLowerCase().includes('engineering rune')
    )
  );
  const allowance = runicAllowanceOpt?.max_points ?? null;

  // Total runic pts on this entry
  const totalRunicPts = [
    ...ri.weaponRunes,
    ...ri.armourRunes,
    ...ri.talismanRunes,
    ...ri.engineeringRunes,
    ...ri.tattooRunes,
  ].reduce((sum, id) => {
    const r = allRunes.find((x) => x.id === id);
    return sum + (r?.points ?? 0);
  }, 0);

  // Master Rune IDs used across the entire army (excluding this entry)
  const otherEntryRuneIds = allEntries
    .filter((e) => e.id !== entry.id)
    .flatMap((e) => {
      const r = e.runicItems;
      if (!r) return [];
      return [...r.weaponRunes, ...r.armourRunes, ...r.talismanRunes, ...r.engineeringRunes, ...r.tattooRunes];
    });
  const armyMasterRunes = new Set(
    otherEntryRuneIds.filter((id) => {
      const r = allRunes.find((x) => x.id === id);
      return r?.name.startsWith('Master Rune');
    })
  );

  function patchRi(patch: Partial<RunicItemState>) {
    updateEntry(armyId, entry.id, { runicItems: { ...ri, ...patch } });
  }

  function addRune(listKey: RuneListKey, runeId: string) {
    const current = ri[listKey] as string[];
    patchRi({ [listKey]: [...current, runeId] });
  }

  function removeRune(listKey: RuneListKey, runeId: string) {
    const current = ri[listKey] as string[];
    const lastIdx = current.lastIndexOf(runeId);
    if (lastIdx === -1) return;
    patchRi({ [listKey]: [...current.slice(0, lastIdx), ...current.slice(lastIdx + 1)] });
  }

  // Returns whether adding one more instance of `rune` to `currentList` is permitted.
  function isRuneDisabled(
    listKey: RuneListKey,
    rune: NonNullable<typeof faction.runic_items>[0],
    currentList: string[]
  ): { disabled: boolean; reason?: string } {
    // Tattoos: unit-specific limit (not Rule of Three)
    if (listKey === 'tattooRunes') {
      if (currentList.length >= tattooLimit) return { disabled: true, reason: `Max ${tattooLimit} tattoo${tattooLimit === 1 ? '' : 's'} for this unit` };
      return { disabled: false };
    }

    // Rule of Three: max 3 runes per item
    if (currentList.length >= 3) return { disabled: true, reason: 'Max 3 runes per item' };

    // Rule of Jealousy: each Master Rune once per army
    if (rune.name.startsWith('Master Rune') && armyMasterRunes.has(rune.id)) {
      return { disabled: true, reason: 'Master Rune already in army' };
    }

    // Rule of Jealousy: only one Master Rune per item; Master Runes cannot be duplicated
    if (rune.name.startsWith('Master Rune')) {
      const hasMaster = currentList.some((id) => allRunes.find((r) => r.id === id)?.name.startsWith('Master Rune'));
      if (hasMaster) return { disabled: true, reason: 'One Master Rune per item' };
    }

    // Points cap
    if (allowance !== null) {
      const remaining = allowance - totalRunicPts;
      if (rune.points > remaining) return { disabled: true, reason: 'Exceeds points allowance' };
    }

    return { disabled: false };
  }

  function renderRuneList(
    label: string,
    listKey: RuneListKey,
    filterFn: (r: NonNullable<typeof faction.runic_items>[0]) => boolean,
    expandKey: string,
    expandedMap: Record<string, boolean>,
    setExpanded: (m: Record<string, boolean>) => void,
  ) {
    const runes = allRunes.filter(filterFn);
    if (runes.length === 0) return null;
    const currentList = ri[listKey] as string[];
    const isExpanded = expandedMap[expandKey] ?? false;
    const selectedPts = currentList.reduce((s, id) => s + (allRunes.find((r) => r.id === id)?.points ?? 0), 0);

    return (
      <div key={listKey} className="flex flex-col">
        <button
          className="flex items-center justify-between w-full text-left py-1"
          onClick={() => setExpanded({ ...expandedMap, [expandKey]: !isExpanded })}
        >
          <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--f-text-3)', opacity: 0.6, fontSize: '0.65rem' }}>
            {isExpanded ? '▼' : '▶'} {label}
          </span>
          {selectedPts > 0 && (
            <span className="text-xs" style={{ color: 'var(--f-text-3)' }}>{selectedPts} pts</span>
          )}
        </button>
        {isExpanded && runes.map((rune) => {
          const count = currentList.filter((id) => id === rune.id).length;
          const hasAny = count > 0;
          const { disabled: addDisabled, reason } = isRuneDisabled(listKey, rune, currentList);
          return (
            <div
              key={rune.id}
              className={`flex items-start gap-2 pt-1.5 border-t ${addDisabled && !hasAny ? 'opacity-40' : ''}`}
              style={{ borderColor: 'var(--f-border)' }}
            >
              {/* −/count/+ stepper */}
              <div className="flex items-center shrink-0 mt-0.5" style={{ gap: '2px' }}>
                <button
                  className="w-5 h-5 rounded text-xs flex items-center justify-center leading-none"
                  style={{
                    border: `1px solid ${hasAny ? 'var(--f-gold)' : 'var(--f-border)'}`,
                    color: hasAny ? 'var(--f-gold)' : 'var(--f-text-3)',
                    opacity: hasAny ? 1 : 0.4,
                    backgroundColor: 'transparent',
                    cursor: hasAny ? 'pointer' : 'default',
                  }}
                  disabled={!hasAny}
                  onClick={() => removeRune(listKey, rune.id)}
                >−</button>
                <span
                  className="w-5 text-center text-xs font-medium"
                  style={{ color: hasAny ? 'var(--f-gold)' : 'var(--f-text-3)', opacity: hasAny ? 1 : 0.4 }}
                >{count}</span>
                <button
                  className="w-5 h-5 rounded text-xs flex items-center justify-center leading-none"
                  style={{
                    border: `1px solid ${addDisabled ? 'var(--f-border)' : 'var(--f-gold)'}`,
                    color: addDisabled ? 'var(--f-text-3)' : 'var(--f-gold)',
                    opacity: addDisabled ? 0.4 : 1,
                    backgroundColor: 'transparent',
                    cursor: addDisabled ? 'default' : 'pointer',
                  }}
                  disabled={addDisabled}
                  onClick={() => addRune(listKey, rune.id)}
                >+</button>
              </div>
              <span className="flex flex-col min-w-0 gap-0.5 flex-1">
                <span className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-medium leading-snug" style={{ color: hasAny ? 'var(--f-text)' : 'var(--f-text-3)' }}>
                    {rune.name} - {rune.points} pts
                  </span>
                  {rune.name.includes('*') && (
                    <span className="text-xs px-1 rounded" style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text-3)', fontSize: '0.6rem' }}>stackable</span>
                  )}
                  {rune.name.startsWith('Master Rune') && (
                    <span className="text-xs px-1 rounded" style={{ backgroundColor: 'var(--f-bg)', color: 'var(--f-text-3)', fontSize: '0.6rem' }}>Master</span>
                  )}
                </span>
                {rune.description && (
                  <span className="text-xs leading-snug" style={{ color: 'var(--f-text-3)' }}>{rune.description}</span>
                )}
                {addDisabled && !hasAny && reason && (
                  <span className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.5 }}>{reason}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Weapon slot options: melee vs ranged
  const hasAnyWeaponRunes = allRunes.some((r) => r.category === 'runic_weapon');
  const hasMeleeSlot = !isWarMachine && hasAnyWeaponRunes;

  // Which weapon runes are available based on selected slot
  function weaponRuneFilter(r: NonNullable<typeof faction.runic_items>[0]) {
    if (r.category !== 'runic_weapon') return false;
    if (!ri.weaponSlot) return false;
    const isRangedSlot = ri.weaponSlot === 'crossbow' || ri.weaponSlot === 'handgun';
    const isRangedRune = r.source === 'arcane_journal';
    return isRangedSlot ? isRangedRune : !isRangedRune;
  }

  // State for expand/collapse - reuse expandedMagicCategories but with runic_ prefix would be cleaner;
  // since we can't easily thread a separate state here, we use a local useState.
  // Actually EntryOptionsPanel already has access via closure - but RunicItemsPicker is separate.
  // We use a local state here.
  const [expandedRunes, setExpandedRunes] = React.useState<Record<string, boolean>>({});

  if (!allRunes.length) return null;
  if (isWarMachine && !allRunes.some((r) => r.category === 'runic_engineering')) return null;
  if (!isWarMachine && !hasTattoos && !allRunes.some((r) => ['runic_weapon', 'runic_armour', 'runic_talisman'].includes(r.category))) return null;

  const overBudget = allowance !== null && totalRunicPts > allowance;

  return (
    <>
      <div className="border-t" style={{ borderColor: 'var(--f-border)' }} />
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>Runic Items</p>
          {allowance !== null && (
            <p className="text-xs" style={{ color: overBudget ? 'var(--f-gold)' : 'var(--f-text-3)' }}>
              {totalRunicPts} / {allowance} pts
            </p>
          )}
        </div>

        {/* Weapon slot picker (characters only) */}
        {hasMeleeSlot && (
          <div className="flex flex-col gap-1">
            <p className="text-xs" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>Inscribe weapon runes on:</p>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(WEAPON_SLOT_LABELS) as (keyof RunicItemState['weaponSlot'] & string)[]).map((slot) => {
                const isActive = ri.weaponSlot === slot;
                return (
                  <button
                    key={slot}
                    onClick={() => {
                      if (isActive) {
                        patchRi({ weaponSlot: undefined, weaponRunes: [] });
                      } else {
                        patchRi({ weaponSlot: slot as RunicItemState['weaponSlot'], weaponRunes: [] });
                      }
                    }}
                    className="text-xs px-2 py-1 rounded border"
                    style={{
                      borderColor: isActive ? 'var(--f-gold)' : 'var(--f-border)',
                      backgroundColor: isActive ? 'var(--f-gold)' : 'transparent',
                      color: isActive ? '#0f1117' : 'var(--f-text-3)',
                    }}
                  >
                    {WEAPON_SLOT_LABELS[slot]}
                  </button>
                );
              })}
            </div>
            {ri.weaponSlot && (
              <p className="text-xs italic" style={{ color: 'var(--f-text-3)', opacity: 0.6 }}>
                {ri.weaponSlot === 'crossbow' || ri.weaponSlot === 'handgun'
                  ? 'Showing Engineers\' Weapon Runes (crossbow/handgun only)'
                  : 'Showing Weapon Runes (hand weapon or great weapon)'}
              </p>
            )}
          </div>
        )}

        {/* Weapon runes */}
        {ri.weaponSlot && renderRuneList(
          'Weapon Runes',
          'weaponRunes',
          weaponRuneFilter,
          'runic_weapon',
          expandedRunes,
          setExpandedRunes,
        )}

        {/* Armour runes (characters only) */}
        {!isWarMachine && renderRuneList(
          'Armour Runes',
          'armourRunes',
          (r) => r.category === 'runic_armour',
          'runic_armour',
          expandedRunes,
          setExpandedRunes,
        )}

        {/* Talisman runes (characters only) */}
        {!isWarMachine && renderRuneList(
          'Talisman Runes',
          'talismanRunes',
          (r) => r.category === 'runic_talisman',
          'runic_talisman',
          expandedRunes,
          setExpandedRunes,
        )}

        {/* Engineering runes (war machines only) */}
        {isWarMachine && renderRuneList(
          'Engineering Runes',
          'engineeringRunes',
          (r) => r.category === 'runic_engineering',
          'runic_engineering',
          expandedRunes,
          setExpandedRunes,
        )}

        {/* Tattoos (Slayer characters) */}
        {hasTattoos && renderRuneList(
          `Runic Tattoos (max ${tattooLimit})`,
          'tattooRunes',
          (r) => r.category === 'runic_tattoo',
          'runic_tattoo',
          expandedRunes,
          setExpandedRunes,
        )}
      </div>
    </>
  );
}

function OptionsSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold" style={{ color: 'var(--f-text-3)' }}>{label}</p>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function OptionCheckbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <span
        className="shrink-0 w-4 h-4 rounded border flex items-center justify-center text-xs"
        style={{
          borderColor: checked ? 'var(--f-gold)' : 'var(--f-border)',
          backgroundColor: checked ? 'var(--f-gold)' : 'transparent',
          color: '#0f1117',
        }}
      >
        {checked ? '✓' : ''}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <span className="text-xs" style={{ color: checked ? 'var(--f-text)' : 'var(--f-text-3)' }}>
        {label}
      </span>
    </label>
  );
}

function RegularOption({
  opt,
  entry,
  toggleOption,
  externalBlock,
  unmetReq,
}: {
  opt: Option;
  entry: ArmyEntry;
  toggleOption: (desc: string, checked: boolean) => void;
  externalBlock?: string;
  unmetReq?: string;
}) {
  const checked = entry.selectedOptions.includes(opt.description);
  const champOnlyMatch = opt.condition?.match(/^(.+) champion only$/);
  const conditionMet = !opt.condition
    || entry.selectedOptions.includes(opt.condition)
    || (champOnlyMatch != null && (entry.includeChampion || entry.selectedOptions.includes(champOnlyMatch[1])));
  const blockedBy = externalBlock ?? (opt.condition_not && entry.selectedOptions.includes(opt.condition_not)
    ? opt.condition_not.replace(/^.+?with the /, '')
    : null);
  // A selected option stays interactive so an invalid state can be unpicked
  const isDisabled = !conditionMet || blockedBy != null || (unmetReq != null && !checked);
  const costPart = opt.points > 0
    ? opt.scope === 'per_model'
      ? ` - +${opt.points} pts/model`
      : ` - +${opt.points} pts`
    : '';
  const reqPart = unmetReq && !checked ? ` (${unmetReq})` : '';
  return (
    <div className={`flex flex-col gap-0.5${isDisabled ? ' opacity-40' : ''}`}>
      <label className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <span
          className="shrink-0 w-4 h-4 rounded border flex items-center justify-center text-xs"
          style={{
            borderColor: checked ? 'var(--f-gold)' : 'var(--f-border)',
            backgroundColor: checked ? 'var(--f-gold)' : 'transparent',
            color: '#0f1117',
          }}
        >
          {checked ? '✓' : ''}
        </span>
        <input
          type="checkbox"
          checked={checked}
          disabled={isDisabled}
          onChange={(e) => !isDisabled && toggleOption(opt.description, e.target.checked)}
          className="sr-only"
        />
        <span className="text-xs" style={{ color: checked ? 'var(--f-text)' : 'var(--f-text-3)' }}>
          {opt.description}{costPart}{reqPart}{blockedBy ? ` - not with the ${blockedBy}` : ''}
        </span>
      </label>
      {opt.replaces && (
        <span className="text-xs italic ml-6" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
          Replaces: {opt.replaces}
        </span>
      )}
      {opt.notes && (
        <span className="text-xs italic ml-6" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
          {opt.notes}
        </span>
      )}
    </div>
  );
}

function ChoiceGroup(props: {
  group: Option;
  entry: ArmyEntry;
  selectChoice: (choices: OptionChoice[], choiceDesc: string | null) => void;
  toggleChoice: (choiceDesc: string) => void;
  armyId: string;
  externalBlock?: string;
  /** Per-choice unmet-prerequisite reason (e.g. 'requires a mount') */
  choiceBlock?: (desc: string) => string | undefined;
}) {
  const { group, entry, selectChoice, toggleChoice } = props;
  const choices = group.choices!;
  const isMulti = !!group.multi_select;
  // Champion-gated groups (e.g. "A Veteran may replace their crossbow with:")
  const champOnlyMatch = group.condition?.match(/^(.+) champion only$/);
  const champUnmet = champOnlyMatch != null
    && !entry.includeChampion
    && !entry.selectedOptions.includes(champOnlyMatch[1]);
  const groupBlockedBy = props.externalBlock ?? (group.condition_not && entry.selectedOptions.includes(group.condition_not)
    ? group.condition_not.replace(/^.+?with the /, '')
    : null);
  if (champUnmet && !choices.some((c) => entry.selectedOptions.includes(c.description))) {
    return (
      <div className="flex flex-col gap-0.5 opacity-40">
        <p className="text-xs font-medium" style={{ color: 'var(--f-text-3)' }}>
          {group.description} (requires the {champOnlyMatch![1]} upgrade)
        </p>
      </div>
    );
  }
  // For radio: only one can be active; for checkbox: any subset
  const selectedDesc = !isMulti
    ? (choices.find((c) => entry.selectedOptions.includes(c.description))?.description ?? null)
    : null;

  if (groupBlockedBy) {
    return (
      <div className="flex flex-col gap-0.5 opacity-40">
        {group.description && (
          <p className="text-xs font-medium" style={{ color: 'var(--f-text-3)' }}>
            {group.description} - not with the {groupBlockedBy}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {group.description && (
        <p className="text-xs font-medium" style={{ color: 'var(--f-text-3)' }}>{group.description}</p>
      )}
      {choices.map((choice) => {
        const isActive = isMulti
          ? entry.selectedOptions.includes(choice.description)
          : selectedDesc === choice.description;
        // Keep an already-selected choice interactive so an invalid state can be unpicked
        const choiceReq = !isActive ? props.choiceBlock?.(choice.description) : undefined;
        const costPart = choice.points > 0
          ? choice.scope === 'per_model'
            ? ` - +${choice.points} pts/model`
            : ` - +${choice.points} pts`
          : '';
        return (
          <label key={choice.description} className={`flex items-center gap-2 ml-2 ${choiceReq ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
            {isMulti ? (
              <span
                className="shrink-0 w-4 h-4 border flex items-center justify-center"
                style={{
                  borderColor: isActive ? 'var(--f-primary)' : 'var(--f-border)',
                  backgroundColor: isActive ? 'var(--f-primary)' : 'transparent',
                  borderRadius: '2px',
                }}
              >
                {isActive && <span style={{ color: '#fff', fontSize: '10px', lineHeight: 1 }}>✓</span>}
              </span>
            ) : (
              <span
                className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                style={{
                  borderColor: isActive ? 'var(--f-primary)' : 'var(--f-border)',
                  backgroundColor: isActive ? 'var(--f-primary)' : 'transparent',
                }}
              >
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
            )}
            <input
              type={isMulti ? 'checkbox' : 'radio'}
              checked={isActive}
              disabled={choiceReq != null}
              onChange={() => isMulti ? toggleChoice(choice.description) : selectChoice(choices, choice.description)}
              className="sr-only"
            />
            <span className="text-xs" style={{ color: isActive ? 'var(--f-text)' : 'var(--f-text-3)' }}>
              {choice.description}{costPart}{choiceReq ? ` (${choiceReq})` : ''}
            </span>
            {choice.notes && (
              <span className="text-xs italic ml-6" style={{ color: 'var(--f-text-3)', opacity: 0.7 }}>
                {choice.notes}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
